import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { AnimatePresence, motion } from "framer-motion";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { SiGoogle, SiApple, SiX } from "react-icons/si";
import { Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Check,
  ArrowLeft,
  ArrowRight,
  MapPin,
  Leaf,
  Shield,
  Gift,
  Calendar,
  CreditCard,
  Sun,
  TreePine,
  RefreshCw,
  Wrench,
  User,
  ShoppingBag,
  Sparkles,
  MessageCircle,
  Crown,
  Mountain,
  Users,
  Truck,
  Home,
  ChevronDown,
  Settings2,
  Pencil,
} from "lucide-react";
import { biomes } from "@shared/schema";

import desertTerrarium from "@assets/generated_images/desert_terrarium_product.png";
import mossTerrarium from "@assets/generated_images/moss_forest_terrarium.png";
import tropicalTerrarium from "@assets/generated_images/geometric_tropical_terrarium.png";
import craftingProcess from "@assets/generated_images/terrarium_crafting_process.png";
import ryanArtisan from "@assets/generated_images/ryan_the_artisan_maker.png";
import heroImage from "@assets/generated_images/lush_terrarium_hero_image.png";

const STEPS = [
  { id: 1, name: "Biome" },
  { id: 2, name: "Delivery" },     // Reserve your week (2 max per week)
  { id: 3, name: "Contact" },
  { id: 4, name: "Location" },
  { id: 5, name: "Package" },      // Base $499 + insurance
  { id: 6, name: "Upgrades" },     // Upsell checkboxes
  { id: 7, name: "Experience" },   // Conditional - only if hasAnyUpsell
  { id: 8, name: "Gift" },
  { id: 9, name: "Payment" },
];

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const OCCASIONS = [
  { value: "birthday", label: "Birthday" },
  { value: "anniversary", label: "Anniversary" },
  { value: "wedding", label: "Wedding" },
  { value: "housewarming", label: "Housewarming" },
  { value: "holiday", label: "Holiday" },
  { value: "thank-you", label: "Thank You" },
  { value: "just-because", label: "Just Because" },
  { value: "other", label: "Other" },
];

const styleImages: Record<string, string> = {
  forest: mossTerrarium,
  desert: desertTerrarium,
  tropical: tropicalTerrarium,
  deluxe: mossTerrarium,
};

function getAvailableWeeks(): { value: string; label: string; weekLabel: string; monthYear: string; startDate: Date }[] {
  const weeks: { value: string; label: string; weekLabel: string; monthYear: string; startDate: Date }[] = [];
  const today = new Date();

  // Start from 2 weeks out (minimum lead time) and show 8 weeks total
  for (let i = 2; i < 10; i++) {
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() + (i * 7) - today.getDay() + 1);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    const formatDate = (date: Date) =>
      date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

    // Calculate week number within the month
    const weekOfMonth = Math.ceil(weekStart.getDate() / 7);
    const monthName = weekStart.toLocaleDateString("en-US", { month: "long" });
    const year = weekStart.getFullYear();

    weeks.push({
      value: weekStart.toISOString().split("T")[0],
      label: `${formatDate(weekStart)} - ${formatDate(weekEnd)}`,
      weekLabel: `Week ${weekOfMonth} of ${monthName}`,
      monthYear: `${monthName} ${year}`,
      startDate: weekStart,
    });
  }

  return weeks;
}

interface CheckoutState {
  step: number;
  biome: string;
  name: string;
  email: string;
  phone: string;
  state: string;
  deliveryWeek: string;
  specialRequests: string;
  isGift: boolean;
  recipientName: string;
  occasion: string;
  giftMessage: string;

  // Extended gift fields
  recipientEmail: string;
  giftDeliveryAddress: string;
  giftDeliveryCity: string;
  giftDeliveryState: string;
  giftDeliveryZip: string;
  useDifferentDeliveryAddress: boolean;
  preferredDeliveryDate: string;
  notifyOnShip: boolean;  // false = keep it a surprise
  giftWrapping: 'standard' | 'premium' | 'none';
  hideGiftReceipt: boolean;  // Generate receipt that hides pricing

  // NEW fields for upsells
  hasPremiumPlants: boolean;    // +$499
  hasCustomDesign: boolean;     // +$999
  experienceType: 'none' | '2-person' | '4-person';

  // Insurance fields
  wantsInsurance: boolean;
  insurancePlan: 'monthly' | 'annual' | 'none';  // monthly=$20/mo, annual=$200/yr, none=take responsibility

  // Location/delivery fields
  isColoradoResident: boolean | null;
  denverDriveTime: string;  // '30min' | '60min' | '90min' | '120min' | etc up to '300min'
  cityName: string;
  qualifiesForDelivery: boolean;  // true if CO resident within 90min of Denver

  // Legacy fields for compatibility
  terrariumStyle: string;
  terrariumStyleName: string;
  terrariumPrice: number;
}

// Step icons mapping
const STEP_ICONS: Record<number, React.ReactNode> = {
  1: <Leaf className="w-4 h-4" />,
  2: <Calendar className="w-4 h-4" />,
  3: <User className="w-4 h-4" />,
  4: <MapPin className="w-4 h-4" />,
  5: <ShoppingBag className="w-4 h-4" />,
  6: <Sparkles className="w-4 h-4" />,
  7: <Mountain className="w-4 h-4" />,
  8: <Gift className="w-4 h-4" />,
  9: <CreditCard className="w-4 h-4" />,
};

function ProgressIndicator({
  currentStep,
  hasAnyUpsell,
  onStepClick
}: {
  currentStep: number;
  hasAnyUpsell?: boolean;
  onStepClick?: (step: number) => void;
}) {
  // Filter out Experience step (7) if neither upsell is selected
  const visibleSteps = STEPS.filter(step => {
    if (step.id === 7 && !hasAnyUpsell) return false;
    return true;
  });

  const handleStepClick = (stepId: number) => {
    // Only allow clicking on completed steps
    if (stepId < currentStep && onStepClick) {
      onStepClick(stepId);
    }
  };

  return (
    <nav
      className="w-full max-w-4xl mx-auto px-4 py-4"
      data-testid="progress-indicator"
      aria-label="Checkout progress"
    >
      {/* Screen reader announcement for current step */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        Step {currentStep} of {visibleSteps.length}: {visibleSteps.find(s => s.id === currentStep)?.name}
      </div>
      <ol className="flex items-center justify-between list-none" role="list">
        {visibleSteps.map((step, index) => {
          const isCompleted = currentStep > step.id;
          const isCurrent = currentStep === step.id;
          const isClickable = isCompleted && onStepClick;
          const stepStatus = isCompleted ? 'completed' : isCurrent ? 'current' : 'upcoming';

          return (
            <li key={step.id} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={() => handleStepClick(step.id)}
                  disabled={!isClickable}
                  aria-label={`Step ${index + 1}: ${step.name}${isCompleted ? ' (completed, click to go back)' : isCurrent ? ' (current step)' : ' (upcoming)'}`}
                  aria-current={isCurrent ? 'step' : undefined}
                  className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all ${
                    isCompleted
                      ? "bg-primary text-primary-foreground cursor-pointer hover:bg-primary/80 hover:scale-105"
                      : isCurrent
                      ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background"
                      : "bg-muted text-muted-foreground cursor-default"
                  }`}
                  data-testid={`progress-step-${step.id}`}
                >
                  <span aria-hidden="true">{STEP_ICONS[step.id]}</span>
                </button>
                <span
                  className={`text-[10px] sm:text-xs mt-1.5 font-medium text-center leading-tight ${
                    currentStep >= step.id ? "text-foreground" : "text-muted-foreground"
                  }`}
                  aria-hidden="true"
                >
                  {step.name}
                </span>
              </div>
              {index < visibleSteps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-1 sm:mx-2 transition-colors ${
                    currentStep > step.id ? "bg-primary" : "bg-muted"
                  }`}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

const CHECKOUT_STORAGE_KEY = "btw_checkout_state";
const CONTACT_STORAGE_KEY = "btw_contact_info";

// Helper to get initial state from localStorage
function getStoredCheckoutState(): Partial<CheckoutState> | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(CHECKOUT_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error("Failed to parse stored checkout state:", e);
  }
  return null;
}

// Helper to get stored contact info from localStorage
function getStoredContactInfo(): { name: string; email: string; phone: string } | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(CONTACT_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error("Failed to parse stored contact info:", e);
  }
  return null;
}

// Helper to save contact info to localStorage immediately
function saveContactToLocalStorage(name: string, email: string, phone: string) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CONTACT_STORAGE_KEY, JSON.stringify({ name, email, phone }));
  } catch (e) {
    console.error("Failed to save contact info:", e);
  }
}

export default function Checkout() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const [isHydrated, setIsHydrated] = useState(false);

  const getInitialStep = () => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const stepParam = params.get("step");
      if (stepParam) {
        const step = parseInt(stepParam, 10);
        if (step >= 1 && step <= 9) {
          window.history.replaceState({}, "", "/checkout");
          return step;
        }
      }
      // Check localStorage for saved step
      const stored = getStoredCheckoutState();
      if (stored?.step && stored.step >= 1 && stored.step <= 9) {
        return stored.step;
      }
    }
    return 1;
  };

  const getInitialState = (): CheckoutState => {
    const stored = getStoredCheckoutState();
    const contact = getStoredContactInfo();

    return {
      step: getInitialStep(),
      // Use stored values or defaults
      biome: stored?.biome || '',
      name: contact?.name || stored?.name || '',
      email: contact?.email || stored?.email || '',
      phone: contact?.phone || stored?.phone || '',
      state: stored?.state || '',
      deliveryWeek: stored?.deliveryWeek || '',
      specialRequests: stored?.specialRequests || '',
      isGift: stored?.isGift || false,
      recipientName: stored?.recipientName || '',
      occasion: stored?.occasion || '',
      giftMessage: stored?.giftMessage || '',
      // Extended gift fields
      recipientEmail: (stored as any)?.recipientEmail || '',
      giftDeliveryAddress: (stored as any)?.giftDeliveryAddress || '',
      giftDeliveryCity: (stored as any)?.giftDeliveryCity || '',
      giftDeliveryState: (stored as any)?.giftDeliveryState || '',
      giftDeliveryZip: (stored as any)?.giftDeliveryZip || '',
      useDifferentDeliveryAddress: (stored as any)?.useDifferentDeliveryAddress || false,
      preferredDeliveryDate: (stored as any)?.preferredDeliveryDate || '',
      notifyOnShip: (stored as any)?.notifyOnShip ?? true,
      giftWrapping: (stored as any)?.giftWrapping || 'standard',
      hideGiftReceipt: (stored as any)?.hideGiftReceipt ?? true,
      hasPremiumPlants: stored?.hasPremiumPlants || false,
      hasCustomDesign: stored?.hasCustomDesign || false,
      experienceType: stored?.experienceType || 'none',
      wantsInsurance: stored?.wantsInsurance ?? true,
      insurancePlan: (stored as any)?.insurancePlan || 'monthly',
      isColoradoResident: (stored as any)?.isColoradoResident ?? null,
      denverDriveTime: (stored as any)?.denverDriveTime || '',
      cityName: (stored as any)?.cityName || '',
      qualifiesForDelivery: (stored as any)?.qualifiesForDelivery ?? true,
      terrariumStyle: stored?.terrariumStyle || '',
      terrariumStyleName: stored?.terrariumStyleName || '',
      terrariumPrice: stored?.terrariumPrice || 499,
    };
  };

  const [checkoutState, setCheckoutState] = useState<CheckoutState>(getInitialState);

  // Fetch user preferences from API if logged in
  const { data: userPrefsData } = useQuery<{
    preferences: { phone?: string; preferredName?: string; state?: string; lastCheckoutState?: CheckoutState } | null;
    user: { email?: string; firstName?: string; lastName?: string } | null;
  }>({
    queryKey: ["/api/user/preferences"],
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Save preferences mutation
  const savePreferencesMutation = useMutation({
    mutationFn: async (data: { phone?: string; preferredName?: string; state?: string; lastCheckoutState?: Partial<CheckoutState> }) => {
      const response = await apiRequest("POST", "/api/user/preferences", data);
      return response.json();
    },
  });

  // Hydrate from user preferences when loaded
  useEffect(() => {
    if (!isHydrated && userPrefsData) {
      const { preferences, user: userData } = userPrefsData;
      const updates: Partial<CheckoutState> = {};

      // Hydrate from user data
      if (userData) {
        if (!checkoutState.email && userData.email) {
          updates.email = userData.email;
        }
        if (!checkoutState.name) {
          const fullName = [userData.firstName, userData.lastName].filter(Boolean).join(" ");
          if (fullName) updates.name = fullName;
        }
      }

      // Hydrate from preferences
      if (preferences) {
        if (!checkoutState.phone && preferences.phone) {
          updates.phone = preferences.phone;
        }
        if (preferences.preferredName && !checkoutState.name) {
          updates.name = preferences.preferredName;
        }
        // Restore last checkout state if we're starting fresh
        if (preferences.lastCheckoutState && checkoutState.step === 1 && !checkoutState.biome) {
          Object.assign(updates, preferences.lastCheckoutState);
        }
      }

      if (Object.keys(updates).length > 0) {
        setCheckoutState(prev => ({ ...prev, ...updates }));
      }
      setIsHydrated(true);
    }
  }, [userPrefsData, isHydrated, checkoutState.email, checkoutState.name, checkoutState.phone, checkoutState.step, checkoutState.biome]);

  // Mark as hydrated for non-authenticated users
  useEffect(() => {
    if (!isAuthenticated && !isHydrated) {
      setIsHydrated(true);
    }
  }, [isAuthenticated, isHydrated]);

  // Persist checkout state to localStorage on every change
  useEffect(() => {
    if (typeof window !== "undefined" && isHydrated) {
      localStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(checkoutState));

      // Also save contact info separately for easy access
      if (checkoutState.name || checkoutState.email || checkoutState.phone) {
        localStorage.setItem(CONTACT_STORAGE_KEY, JSON.stringify({
          name: checkoutState.name,
          email: checkoutState.email,
          phone: checkoutState.phone,
        }));
      }
    }
  }, [checkoutState, isHydrated]);

  // Save to API when contact info changes (for logged-in users)
  const saveContactToAPI = (name: string, email: string, phone: string) => {
    if (isAuthenticated) {
      savePreferencesMutation.mutate({
        phone,
        preferredName: name,
        lastCheckoutState: checkoutState,
      });
    }
  };

  const { data: blockedWeeks = [] } = useQuery<string[]>({
    queryKey: ["/api/blocked-weeks"],
  });

  const checkoutMutation = useMutation({
    mutationFn: async () => {
      // Use Life Itself Studio checkout format for proper line item generation
      const response = await apiRequest("POST", "/api/create-checkout-session", {
        // Customer info with full contact details
        customer: {
          name: checkoutState.name,
          email: checkoutState.email,
          phone: checkoutState.phone,
          state: checkoutState.state,
        },
        // Product selections (all at root level for Life Itself Studio format)
        biome: checkoutState.biome as "forest" | "desert",
        hasPremiumPlants: checkoutState.hasPremiumPlants,
        hasCustomDesign: checkoutState.hasCustomDesign,
        experienceType: checkoutState.experienceType,
        deliveryWeek: checkoutState.deliveryWeek,
        // Gift options - extended fields
        isGift: checkoutState.isGift,
        giftRecipientName: checkoutState.recipientName,
        giftRecipientEmail: checkoutState.recipientEmail,
        giftOccasion: checkoutState.occasion,
        giftMessage: checkoutState.giftMessage,
        useDifferentDeliveryAddress: checkoutState.useDifferentDeliveryAddress,
        giftDeliveryAddress: checkoutState.giftDeliveryAddress,
        giftDeliveryCity: checkoutState.giftDeliveryCity,
        giftDeliveryState: checkoutState.giftDeliveryState,
        giftDeliveryZip: checkoutState.giftDeliveryZip,
        preferredDeliveryDate: checkoutState.preferredDeliveryDate,
        notifyOnShip: checkoutState.notifyOnShip,
        giftWrapping: checkoutState.giftWrapping,
        hideGiftReceipt: checkoutState.hideGiftReceipt,
        specialRequests: checkoutState.specialRequests,
      });
      return response.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Checkout Error",
        description: error.message || "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Reserve mutation for non-qualifying users ($99 reservation fee)
  const reserveMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/create-reservation", {
        customer: {
          name: checkoutState.name,
          email: checkoutState.email,
          phone: checkoutState.phone,
          state: checkoutState.state,
        },
        biome: checkoutState.biome,
        hasPremiumPlants: checkoutState.hasPremiumPlants,
        hasCustomDesign: checkoutState.hasCustomDesign,
        experienceType: checkoutState.experienceType,
        deliveryWeek: checkoutState.deliveryWeek,
        // Gift options - extended fields
        isGift: checkoutState.isGift,
        giftRecipientName: checkoutState.recipientName,
        giftRecipientEmail: checkoutState.recipientEmail,
        giftOccasion: checkoutState.occasion,
        giftMessage: checkoutState.giftMessage,
        useDifferentDeliveryAddress: checkoutState.useDifferentDeliveryAddress,
        giftDeliveryAddress: checkoutState.giftDeliveryAddress,
        giftDeliveryCity: checkoutState.giftDeliveryCity,
        giftDeliveryState: checkoutState.giftDeliveryState,
        giftDeliveryZip: checkoutState.giftDeliveryZip,
        preferredDeliveryDate: checkoutState.preferredDeliveryDate,
        notifyOnShip: checkoutState.notifyOnShip,
        giftWrapping: checkoutState.giftWrapping,
        hideGiftReceipt: checkoutState.hideGiftReceipt,
        insurancePlan: checkoutState.insurancePlan,
        denverDriveTime: checkoutState.denverDriveTime,
        cityName: checkoutState.cityName,
        reservationFee: 99,
      });
      return response.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Reservation Error",
        description: error.message || "Failed to create reservation. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateState = (updates: Partial<CheckoutState>) => {
    setCheckoutState((prev) => ({ ...prev, ...updates }));
  };

  const goToStep = (step: number) => {
    updateState({ step });
  };

  // Calculate total price dynamically
  const calculateTotal = () => {
    let total = 499; // Base
    if (checkoutState.hasPremiumPlants) total += 499;
    if (checkoutState.hasCustomDesign) total += 999;
    if (checkoutState.experienceType === '2-person') total += 1501;
    if (checkoutState.experienceType === '4-person') total += 3501;
    return total;
  };

  // Check if either upsell is selected (for showing Experience step)
  const hasAnyUpsell = checkoutState.hasPremiumPlants || checkoutState.hasCustomDesign;

  // Handle next step with skip logic
  const handleNext = (fromStep: number) => {
    let nextStep = fromStep + 1;

    // Skip Step 7 (Experience) if neither upsell is selected
    if (nextStep === 7 && !hasAnyUpsell) {
      nextStep = 8;
    }

    updateState({ step: nextStep });
  };

  // Handle back step with skip logic
  const handleBack = (fromStep: number) => {
    let prevStep = fromStep - 1;

    // Skip Step 7 (Experience) when going back if neither upsell is selected
    if (prevStep === 7 && !hasAnyUpsell) {
      prevStep = 6;
    }

    updateState({ step: prevStep });
  };

  const renderStep = () => {
    switch (checkoutState.step) {
      case 1:
        return <Step1Biome checkoutState={checkoutState} updateState={updateState} setLocation={setLocation} />;
      case 2:
        return (
          <Step2Delivery
            checkoutState={checkoutState}
            updateState={updateState}
            goToStep={goToStep}
            blockedWeeks={blockedWeeks}
          />
        );
      case 3:
        return <Step3Contact checkoutState={checkoutState} updateState={updateState} goToStep={goToStep} saveContactToAPI={saveContactToAPI} />;
      case 4:
        return <Step4State checkoutState={checkoutState} updateState={updateState} goToStep={goToStep} setLocation={setLocation} />;
      case 5:
        return <Step5Package checkoutState={checkoutState} updateState={updateState} goToStep={goToStep} />;
      case 6:
        return <Step6Upgrades checkoutState={checkoutState} updateState={updateState} goToStep={goToStep} handleNext={handleNext} />;
      case 7:
        return <Step7Experience checkoutState={checkoutState} updateState={updateState} goToStep={goToStep} handleNext={handleNext} handleBack={handleBack} />;
      case 8:
        return <Step8Gift checkoutState={checkoutState} updateState={updateState} goToStep={goToStep} handleBack={handleBack} />;
      case 9:
        return (
          <Step9Payment
            checkoutState={checkoutState}
            updateState={updateState}
            goToStep={goToStep}
            handleBack={handleBack}
            calculateTotal={calculateTotal}
            onSubmit={() => checkoutMutation.mutate()}
            onReserve={() => reserveMutation.mutate()}
            isPending={checkoutMutation.isPending || reserveMutation.isPending}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Skip to checkout form link for keyboard/screen reader users */}
      <a
        href="#checkout-form"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-1/2 focus:-translate-x-1/2 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to checkout form
      </a>
      <div className="fixed top-4 left-4 z-50">
        <a href="/" className="font-serif text-xl font-semibold tracking-tight" data-testid="link-home" aria-label="Life Itself Studio - Return to homepage">
          Life Itself Studio
        </a>
      </div>
      <div className="pt-16">
        <ProgressIndicator currentStep={checkoutState.step} hasAnyUpsell={hasAnyUpsell} onStepClick={goToStep} />
      </div>
      <main id="checkout-form" tabIndex={-1} aria-label="Checkout form">
        <AnimatePresence mode="wait">
          <motion.div
            key={checkoutState.step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}

// All 4 biome options for Step 1
const biomeOptions = [
  {
    id: "desert",
    name: "Desert",
    description: "Succulents, cacti, and beautiful sand formations",
    icon: "sun",
  },
  {
    id: "forest",
    name: "Forest",
    description: "Lush woodland with moss, ferns, and shade-loving plants",
    icon: "tree",
  },
  {
    id: "tropical",
    name: "Tropical",
    description: "Vibrant tropical plants in a geometric vessel",
    icon: "leaf",
  },
  {
    id: "deluxe",
    name: "Deluxe Custom",
    description: "A larger, elaborate creation tailored to your vision",
    icon: "sparkle",
  },
];

// Step 1: Biome Selection (All 4 biome cards)
function Step1Biome({
  checkoutState,
  updateState,
  setLocation,
}: {
  checkoutState: CheckoutState;
  updateState: (updates: Partial<CheckoutState>) => void;
  setLocation: (path: string) => void;
}) {
  const [biome, setBiome] = useState<string>(checkoutState.biome || "forest");
  const [error, setError] = useState<string | null>(null);

  const handleContinue = () => {
    if (!biome) {
      setError("Please select a biome");
      return;
    }
    updateState({
      biome,
      step: 2,
    });
  };

  const getIcon = (iconType: string) => {
    switch (iconType) {
      case "sun":
        return <Sun className="w-5 h-5 text-amber-300" />;
      case "tree":
        return <TreePine className="w-5 h-5 text-green-300" />;
      case "leaf":
        return <Leaf className="w-5 h-5 text-emerald-300" />;
      case "sparkle":
        return <Sparkles className="w-5 h-5 text-violet-300" />;
      default:
        return <Leaf className="w-5 h-5 text-green-300" />;
    }
  };

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-5xl"
      >
        <div className="text-center mb-8">
          <Leaf className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-serif text-3xl md:text-4xl font-semibold mb-3">Choose Your Biome</h1>
          <p className="text-muted-foreground">Select the ecosystem that speaks to you</p>
          <p className="text-sm text-muted-foreground/70 mt-2">You can change this at any time later</p>
        </div>

        <div className="mb-8" role="radiogroup" aria-label="Choose your terrarium biome" aria-required="true">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto">
            {biomeOptions.map((b) => (
              <div
                key={b.id}
                role="radio"
                aria-checked={biome === b.id}
                aria-label={`${b.name}: ${b.description}`}
                tabIndex={0}
                className={`relative aspect-[3/4] rounded-xl overflow-hidden cursor-pointer transition-all duration-300 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  biome === b.id
                    ? "ring-3 ring-primary ring-offset-2 ring-offset-background scale-[1.02]"
                    : "hover:scale-[1.01]"
                }`}
                onClick={() => {
                  setBiome(b.id);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setBiome(b.id);
                    setError(null);
                  }
                }}
                data-testid={`biome-${b.id}`}
              >
                <OptimizedImage
                  src={styleImages[b.id]}
                  alt=""
                  fill
                  objectFit="cover"
                  priority
                  className="absolute inset-0"
                  aria-hidden="true"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10" aria-hidden="true" />

                {biome === b.id && (
                  <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-lg z-20" aria-hidden="true">
                    <Check className="w-5 h-5 text-primary-foreground" />
                  </div>
                )}

                <div className="absolute bottom-0 left-0 right-0 p-4 text-white z-20">
                  <div className="flex items-center gap-2 mb-2">
                    <span aria-hidden="true">{getIcon(b.icon)}</span>
                    <h3 className="font-serif text-lg sm:text-xl font-semibold">{b.name}</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-white/80 line-clamp-2">{b.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-destructive text-center mb-4" role="alert" aria-live="assertive">{error}</p>}

        <div className="flex gap-4 max-w-md mx-auto">
          <Button variant="outline" onClick={() => setLocation("/")} className="flex-1" data-testid="button-back" aria-label="Go back to homepage">
            <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
            Back
          </Button>
          <Button onClick={handleContinue} className="flex-1" data-testid="button-continue" aria-label="Continue to delivery options">
            Continue
            <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

// Step 3: Contact Information (Phone, Email, Name)
function Step3Contact({
  checkoutState,
  updateState,
  goToStep,
  saveContactToAPI,
}: {
  checkoutState: CheckoutState;
  updateState: (updates: Partial<CheckoutState>) => void;
  goToStep: (step: number) => void;
  saveContactToAPI: (name: string, email: string, phone: string) => void;
}) {
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const [name, setName] = useState(checkoutState.name);
  const [email, setEmail] = useState(checkoutState.email);
  const [phone, setPhone] = useState(checkoutState.phone);
  const [errors, setErrors] = useState<{ name?: string; email?: string; phone?: string }>({});
  const [hasAutoFilled, setHasAutoFilled] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user && !hasAutoFilled) {
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
      if (fullName && !name) {
        setName(fullName);
      }
      if (user.email && !email) {
        setEmail(user.email);
      }
      setHasAutoFilled(true);
    }
  }, [isAuthenticated, user, hasAutoFilled, name, email]);

  // Save to localStorage on every keystroke
  useEffect(() => {
    saveContactToLocalStorage(name, email, phone);
  }, [name, email, phone]);

  const handleLoginWithReplit = () => {
    const returnUrl = encodeURIComponent("/checkout?step=2");
    window.location.href = `/api/login?returnTo=${returnUrl}`;
  };

  const handleContinue = () => {
    const newErrors: typeof errors = {};

    if (!name || name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!phone || phone.length < 10) {
      newErrors.phone = "Please enter a valid phone number";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Save contact info to API for logged-in users
      saveContactToAPI(name, email, phone);
      updateState({ name, email, phone, step: 4 });
    }
  };

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <User className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-serif text-3xl md:text-4xl font-semibold mb-3">Contact Information</h1>
          <p className="text-muted-foreground">Tell us how to reach you</p>
        </div>

        <Card className="p-6 md:p-8">
          {!isAuthenticated && !authLoading && (
            <div className="mb-6">
              <Button
                variant="outline"
                onClick={handleLoginWithReplit}
                className="w-full flex-col py-4 h-auto gap-3"
                data-testid="button-login-replit"
              >
                <span className="text-sm font-medium">Log In / Create Account With</span>
                <span className="text-sm">Google, Apple, X or Email</span>
                <div className="flex items-center gap-4 mt-1">
                  <SiGoogle className="w-5 h-5" />
                  <SiApple className="w-5 h-5" />
                  <SiX className="w-5 h-5" />
                  <Mail className="w-5 h-5" />
                </div>
              </Button>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or enter manually</span>
                </div>
              </div>
            </div>
          )}

          {isAuthenticated && user && (
            <div className="mb-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-sm text-muted-foreground">
                Signed in as <span className="font-medium text-foreground">{user.email}</span>
              </p>
            </div>
          )}

          <div className="space-y-4 mb-6" role="group" aria-label="Contact information form">
            <div className="space-y-2">
              <Label htmlFor="checkout-phone">Phone Number *</Label>
              <Input
                id="checkout-phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={errors.phone ? "border-destructive" : ""}
                aria-invalid={!!errors.phone}
                aria-describedby={errors.phone ? "checkout-phone-error" : undefined}
                aria-required="true"
                data-testid="input-phone"
              />
              {errors.phone && <p id="checkout-phone-error" className="text-sm text-destructive" role="alert" aria-live="assertive">{errors.phone}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="checkout-email">Email *</Label>
              <Input
                id="checkout-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={errors.email ? "border-destructive" : ""}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "checkout-email-error" : undefined}
                aria-required="true"
                data-testid="input-email"
              />
              {errors.email && <p id="checkout-email-error" className="text-sm text-destructive" role="alert" aria-live="assertive">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="checkout-name">Full Name *</Label>
              <Input
                id="checkout-name"
                type="text"
                placeholder="John Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={errors.name ? "border-destructive" : ""}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? "checkout-name-error" : undefined}
                aria-required="true"
                data-testid="input-name"
              />
              {errors.name && <p id="checkout-name-error" className="text-sm text-destructive" role="alert" aria-live="assertive">{errors.name}</p>}
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button variant="outline" onClick={() => goToStep(2)} className="flex-1" data-testid="button-back" aria-label="Go back to delivery options">
              <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
              Back
            </Button>
            <Button onClick={handleContinue} className="flex-1" data-testid="button-continue" aria-label="Continue to location selection">
              Continue
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
            </Button>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}

// Month names for visit planning
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

// Drive time options from Denver (30 min increments up to 5 hours)
const DENVER_DRIVE_TIMES = [
  { value: "30min", label: "30 minutes", qualifies: true },
  { value: "60min", label: "1 hour", qualifies: true },
  { value: "90min", label: "1.5 hours", qualifies: true },
  { value: "120min", label: "2 hours", qualifies: false },
  { value: "150min", label: "2.5 hours", qualifies: false },
  { value: "180min", label: "3 hours", qualifies: false },
  { value: "210min", label: "3.5 hours", qualifies: false },
  { value: "240min", label: "4 hours", qualifies: false },
  { value: "270min", label: "4.5 hours", qualifies: false },
  { value: "300min", label: "5 hours", qualifies: false },
];

// Colorado destinations
const CO_DESTINATIONS = [
  { id: "denver", label: "Denver" },
  { id: "aspen", label: "Aspen" },
  { id: "colorado-springs", label: "Colorado Springs" },
  { id: "telluride", label: "Telluride" },
  { id: "other", label: "Other" },
];

// Step 4: Location Check (Colorado continues, others can design but join waitlist)
function Step4State({
  checkoutState,
  updateState,
  goToStep,
  setLocation,
}: {
  checkoutState: CheckoutState;
  updateState: (updates: Partial<CheckoutState>) => void;
  goToStep: (step: number) => void;
  setLocation: (path: string) => void;
}) {
  // Pre-fill USA and Colorado by default
  const [isInUSA, setIsInUSA] = useState<boolean>(true);
  const [state, setState] = useState(checkoutState.state || "CO");
  const [country, setCountry] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Colorado-specific: drive time from Denver
  const [driveTime, setDriveTime] = useState(checkoutState.denverDriveTime || '');
  const [cityName, setCityName] = useState(checkoutState.cityName || '');

  // Planning to visit Colorado? (yes/no/null) - for non-Colorado users
  const [planningToVisit, setPlanningToVisit] = useState<boolean | null>(null);

  // Visit planning state (shown when planningToVisit is true)
  const [visitMonth, setVisitMonth] = useState<string | null>(null);
  const [visitYear, setVisitYear] = useState<number | null>(null);
  const [destination, setDestination] = useState<string | null>(null);
  const [otherDestination, setOtherDestination] = useState("");

  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear + 1, currentYear + 2];

  // Check if Colorado is selected
  const isColorado = isInUSA && state === "CO";

  // Check if selected drive time qualifies for delivery (90 min or less)
  const selectedDriveTimeOption = DENVER_DRIVE_TIMES.find(t => t.value === driveTime);
  const qualifiesForDelivery = isColorado && selectedDriveTimeOption?.qualifies === true;
  const showCityInput = isColorado && driveTime && !selectedDriveTimeOption?.qualifies;

  // Determine if we should show the visit question (non-Colorado USA or international)
  const isNonColorado = (isInUSA && state && state !== "CO") || (!isInUSA && country.trim());

  // Get friendly state name
  const getStateName = () => {
    if (!isInUSA) return country || "your country";
    const stateObj = US_STATES.find(s => s.value === state);
    return stateObj?.label || state;
  };

  // Add to waitlist in background (doesn't redirect, just saves)
  const waitlistMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      email: string;
      phone: string;
      state: string;
      biomePreference: string;
      driveTime?: string;
      cityName?: string;
      visitMonth?: string;
      visitYear?: number;
      destination?: string;
      country?: string;
      isInternational?: boolean;
      notifyWhenShipping?: boolean;
    }) => {
      const response = await apiRequest("POST", "/api/waitlist", data);
      return response.json();
    },
  });

  const handleContinue = () => {
    // International users need country
    if (!isInUSA) {
      if (!country.trim()) {
        setError("Please enter your country");
        return;
      }
    }

    // USA users need state
    if (isInUSA && !state) {
      setError("Please select your state");
      return;
    }

    // Colorado users need to answer drive time question
    if (isColorado) {
      if (!driveTime) {
        setError("Please select your distance from Denver");
        return;
      }

      // Non-qualifying Colorado residents need city name
      if (!selectedDriveTimeOption?.qualifies && !cityName.trim()) {
        setError("Please enter your city");
        return;
      }

      const qualifies = selectedDriveTimeOption?.qualifies === true;

      // If not qualifying, add to waitlist
      if (!qualifies) {
        waitlistMutation.mutate({
          name: checkoutState.name,
          email: checkoutState.email,
          phone: checkoutState.phone,
          state: "CO",
          biomePreference: checkoutState.biome,
          driveTime,
          cityName,
          notifyWhenShipping: true,
        });
      }

      updateState({
        state: "CO",
        isColoradoResident: true,
        denverDriveTime: driveTime,
        cityName: cityName,
        qualifiesForDelivery: qualifies,
        step: 5,
      });
      return;
    }

    // Non-Colorado users need to answer the visit question
    if (planningToVisit === null) {
      setError("Please let us know if you're planning to visit Colorado");
      return;
    }

    // If planning to visit, validate visit info
    if (planningToVisit) {
      if (!visitMonth || !visitYear || !destination) {
        setError("Please tell us when you'll visit Colorado");
        return;
      }
      if (destination === "other" && !otherDestination.trim()) {
        setError("Please enter your destination");
        return;
      }
    }

    // Add to waitlist in background
    waitlistMutation.mutate({
      name: checkoutState.name,
      email: checkoutState.email,
      phone: checkoutState.phone,
      state: isInUSA ? state : "International",
      biomePreference: checkoutState.biome,
      visitMonth: planningToVisit ? visitMonth || undefined : undefined,
      visitYear: planningToVisit ? visitYear || undefined : undefined,
      destination: planningToVisit ? (destination === "other" ? otherDestination : destination) || undefined : undefined,
      country: isInUSA ? "USA" : country,
      isInternational: !isInUSA,
      notifyWhenShipping: !planningToVisit,
    });

    // Let them continue designing regardless but mark as not qualifying
    updateState({
      state: isInUSA ? state : "International",
      isColoradoResident: false,
      qualifiesForDelivery: false,
      step: 5,
    });
  };

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
      >
        <div className="text-center mb-8">
          <MapPin className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-serif text-3xl md:text-4xl font-semibold mb-3">Where Are You Based?</h1>
          <p className="text-muted-foreground">We currently deliver to Colorado, but we're expanding soon!</p>
        </div>

        <Card className="p-6 md:p-8">
          <div className="space-y-6">
            {/* USA Toggle */}
            <div className="space-y-3">
              <Label>Are you in the United States?</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant={isInUSA ? "default" : "outline"}
                  onClick={() => {
                    setIsInUSA(true);
                    if (!state) setState("CO");
                    setError(null);
                  }}
                  className="h-12"
                  data-testid="button-in-usa"
                >
                  Yes, in the USA
                </Button>
                <Button
                  type="button"
                  variant={!isInUSA ? "default" : "outline"}
                  onClick={() => {
                    setIsInUSA(false);
                    setState("");
                    setDriveTime('');
                    setCityName('');
                    setError(null);
                  }}
                  className="h-12"
                  data-testid="button-outside-usa"
                >
                  Outside the USA
                </Button>
              </div>
            </div>

            {/* State Selection (USA only) */}
            <AnimatePresence>
              {isInUSA === true && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-2"
                >
                  <Label htmlFor="state">Which state?</Label>
                  <Select
                    value={state}
                    onValueChange={(value) => {
                      setState(value);
                      setDriveTime('');
                      setCityName('');
                      setError(null);
                    }}
                  >
                    <SelectTrigger data-testid="select-state" className={error && !state ? "border-destructive" : ""}>
                      <SelectValue placeholder="Select your state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((s) => (
                        <SelectItem key={s.value} value={s.value} data-testid={`state-option-${s.value}`}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Colorado-specific: Drive Time Selection */}
            <AnimatePresence>
              {isColorado && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4 pt-4 border-t"
                >
                  <div className="space-y-2">
                    <Label>How far are you from Denver, CO?</Label>
                    <Select
                      value={driveTime}
                      onValueChange={(value) => {
                        setDriveTime(value);
                        setCityName('');
                        setError(null);
                      }}
                    >
                      <SelectTrigger data-testid="select-drive-time" className={error && !driveTime ? "border-destructive" : ""}>
                        <SelectValue placeholder="Select drive time from Denver" />
                      </SelectTrigger>
                      <SelectContent>
                        {DENVER_DRIVE_TIMES.map((time) => (
                          <SelectItem key={time.value} value={time.value} data-testid={`drive-time-${time.value}`}>
                            {time.label} {time.qualifies && "(Qualifies for delivery)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Qualifying message */}
                  {driveTime && selectedDriveTimeOption?.qualifies && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-center"
                    >
                      <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">
                        Great news! You qualify for delivery.
                      </p>
                    </motion.div>
                  )}

                  {/* Non-qualifying: City input and disclaimer */}
                  {showCityInput && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-4"
                    >
                      <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                        <Truck className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                        <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
                          Life Itself Studio may be able to deliver to you for an additional fee. Please input your city and click "Submit Delivery Inquiry" and we will get back to you.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="city">Your City</Label>
                        <Input
                          id="city"
                          placeholder="Enter your city name"
                          value={cityName}
                          onChange={(e) => {
                            setCityName(e.target.value);
                            setError(null);
                          }}
                          className={error && !cityName.trim() ? "border-destructive" : ""}
                          data-testid="input-city"
                        />
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Country Input (International only) */}
            <AnimatePresence>
              {!isInUSA && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-2"
                >
                  <Label htmlFor="country">Which country?</Label>
                  <Input
                    id="country"
                    placeholder="Enter your country"
                    value={country}
                    onChange={(e) => {
                      setCountry(e.target.value);
                      setError(null);
                    }}
                    className={error && !country.trim() ? "border-destructive" : ""}
                    data-testid="input-country"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Visit Planning Section (shown when non-Colorado USA or international) */}
            <AnimatePresence>
              {isNonColorado && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-5 pt-4 border-t"
                >
                  {/* Planning to Visit Toggle */}
                  <div className="space-y-3">
                    <Label>Are you planning to visit Colorado?</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        type="button"
                        variant={planningToVisit === true ? "default" : "outline"}
                        onClick={() => {
                          setPlanningToVisit(true);
                          setError(null);
                        }}
                        className="h-12"
                        data-testid="button-planning-yes"
                      >
                        Yes, I'm visiting!
                      </Button>
                      <Button
                        type="button"
                        variant={planningToVisit === false ? "default" : "outline"}
                        onClick={() => {
                          setPlanningToVisit(false);
                          setError(null);
                        }}
                        className="h-12"
                        data-testid="button-planning-no"
                      >
                        Not right now
                      </Button>
                    </div>
                  </div>

                  {/* If NOT planning to visit - show shipping notification message */}
                  <AnimatePresence>
                    {planningToVisit === false && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="p-4 rounded-lg bg-primary/5 border border-primary/20 text-center"
                      >
                        <Truck className="w-8 h-8 text-primary mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">
                          No problem! We'll remind you when we start shipping to{" "}
                          <span className="font-medium text-foreground">{getStateName()}</span>!
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          You can still continue designing your dream terrarium.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* If planning to visit - show visit details */}
                  <AnimatePresence>
                    {planningToVisit === true && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4"
                      >
                        <div className="text-center">
                          <Calendar className="w-8 h-8 text-primary mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">We'll have your terrarium ready when you arrive!</p>
                        </div>

                        {/* Month Selection - 4x3 Grid */}
                        <div className="space-y-2">
                          <Label className="text-sm">When will you visit?</Label>
                          <div className="grid grid-cols-4 gap-1.5">
                            {MONTHS.map((month) => (
                              <Button
                                key={month}
                                type="button"
                                variant={visitMonth === month ? "default" : "outline"}
                                size="sm"
                                onClick={() => {
                                  setVisitMonth(month);
                                  setError(null);
                                }}
                                className="text-xs h-8"
                                data-testid={`month-${month.toLowerCase()}`}
                              >
                                {month}
                              </Button>
                            ))}
                          </div>
                        </div>

                        {/* Year Selection */}
                        <div className="space-y-2">
                          <Label className="text-sm">Which year?</Label>
                          <div className="grid grid-cols-3 gap-2">
                            {years.map((year) => (
                              <Button
                                key={year}
                                type="button"
                                variant={visitYear === year ? "default" : "outline"}
                                size="sm"
                                onClick={() => {
                                  setVisitYear(year);
                                  setError(null);
                                }}
                                data-testid={`year-${year}`}
                              >
                                {year}
                              </Button>
                            ))}
                          </div>
                        </div>

                        {/* Destination Selection */}
                        <div className="space-y-2">
                          <Label className="text-sm">Where in Colorado?</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {CO_DESTINATIONS.map((dest) => (
                              <Button
                                key={dest.id}
                                type="button"
                                variant={destination === dest.id ? "default" : "outline"}
                                size="sm"
                                onClick={() => {
                                  setDestination(dest.id);
                                  setError(null);
                                }}
                                className={dest.id === "other" ? "col-span-2" : ""}
                                data-testid={`destination-${dest.id}`}
                              >
                                {dest.label}
                              </Button>
                            ))}
                          </div>

                          {/* Other destination input */}
                          <AnimatePresence>
                            {destination === "other" && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <Input
                                  placeholder="Enter your destination..."
                                  value={otherDestination}
                                  onChange={(e) => {
                                    setOtherDestination(e.target.value);
                                    setError(null);
                                  }}
                                  className="mt-2"
                                  data-testid="input-other-destination"
                                />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>

                        {/* Selected summary */}
                        {visitMonth && visitYear && destination && (
                          <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-center">
                            <p className="text-sm text-muted-foreground">
                              We'll reach out before your{" "}
                              <span className="font-medium text-foreground">
                                {visitMonth} {visitYear}
                              </span>{" "}
                              visit to{" "}
                              <span className="font-medium text-foreground">
                                {destination === "other" ? otherDestination || "..." : CO_DESTINATIONS.find(d => d.id === destination)?.label}
                              </span>
                            </p>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex gap-4 pt-4">
              <Button variant="outline" onClick={() => goToStep(3)} className="flex-1" data-testid="button-back">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleContinue}
                className="flex-1"
                data-testid="button-continue"
                disabled={waitlistMutation.isPending}
              >
                {showCityInput && cityName.trim() ? "Submit Delivery Inquiry" : (waitlistMutation.isPending ? "Saving..." : "Continue")}
                {!waitlistMutation.isPending && !(showCityInput && cityName.trim()) && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}

// Step 5: Price Tier Selection - $499 Base, $999 Premium, $1999 Custom
function Step5Package({
  checkoutState,
  updateState,
  goToStep,
}: {
  checkoutState: CheckoutState;
  updateState: (updates: Partial<CheckoutState>) => void;
  goToStep: (step: number) => void;
}) {
  const [selectedTier, setSelectedTier] = useState<"base" | "premium" | "custom">("base");
  const [hoveredTier, setHoveredTier] = useState<"base" | "premium" | "custom" | null>(null);
  const [showBreakdown, setShowBreakdown] = useState<"base" | "premium" | "custom" | null>(null);

  const biomeName = checkoutState.biome === "desert" ? "Desert" : "Forest";

  // Price tier data with compelling value propositions
  const tiers = [
    {
      id: "base" as const,
      name: "Base",
      tagline: "Perfect Starting Point",
      price: 499,
      description: "Choice of two starter biomes - Desert/Cactus or Lush/Fern",
      icon: <Leaf className="w-6 h-6" />,
      color: "text-emerald-600",
      gradientFrom: "from-emerald-500/10",
      gradientTo: "to-green-500/5",
      borderColor: "border-emerald-200 dark:border-emerald-800",
      features: [
        { text: "Handcrafted glass vessel", included: true },
        { text: "Two biome options", included: true },
        { text: "Basic plant selection", included: true },
        { text: "Care guide included", included: true },
        { text: "Colorado local delivery", included: true },
        { text: "Rare plant varieties", included: false },
        { text: "Custom design consultation", included: false },
        { text: "Personal delivery by Ryan", included: false },
      ],
      detailedBreakdown: [
        { title: "What You Get", items: ["6-inch premium glass terrarium vessel", "Hand-selected plants for your biome", "Natural substrate and drainage layers", "Decorative stones and moss", "Digital care guide"] },
        { title: "Biome Options", items: ["Desert: Succulents, cacti, sand formations", "Forest: Moss, ferns, woodland plants"] },
      ],
    },
    {
      id: "premium" as const,
      name: "Premium",
      tagline: "Most Popular Choice",
      price: 999,
      description: "Upgraded materials, complex plant arrangements, additional customization",
      icon: <Crown className="w-6 h-6" />,
      color: "text-violet-600",
      gradientFrom: "from-violet-500/10",
      gradientTo: "to-purple-500/5",
      borderColor: "border-violet-200 dark:border-violet-800",
      popular: true,
      features: [
        { text: "Handcrafted glass vessel", included: true },
        { text: "All biome options", included: true },
        { text: "Rare plant varieties", included: true },
        { text: "Extended care guide", included: true },
        { text: "Priority Colorado delivery", included: true },
        { text: "Decorative miniatures", included: true },
        { text: "Custom design consultation", included: false },
        { text: "Personal delivery by Ryan", included: false },
      ],
      detailedBreakdown: [
        { title: "Premium Upgrades", items: ["8-inch premium artisan vessel", "Rare and exotic plant specimens", "Unique shape and design options", "Premium decorative accents", "Extended 1-year care guarantee"] },
        { title: "Additional Perks", items: ["Priority scheduling", "Detailed video care tutorial", "Quarterly care tips email"] },
      ],
    },
    {
      id: "custom" as const,
      name: "Custom",
      tagline: "White-Glove Experience",
      price: 1999,
      description: "Personal consultation call with Ryan for your dream terrarium",
      icon: <Sparkles className="w-6 h-6" />,
      color: "text-amber-600",
      gradientFrom: "from-amber-500/10",
      gradientTo: "to-orange-500/5",
      borderColor: "border-amber-200 dark:border-amber-800",
      features: [
        { text: "Custom vessel selection", included: true },
        { text: "Any biome or hybrid", included: true },
        { text: "Rare plant varieties", included: true },
        { text: "1-hour design consultation", included: true },
        { text: "Personal delivery & setup", included: true },
        { text: "Lifetime care support", included: true },
        { text: "Premium miniatures", included: true },
        { text: "Photo documentation", included: true },
      ],
      detailedBreakdown: [
        { title: "The Custom Experience", items: ["1-hour personal consultation with Ryan", "Completely custom vessel selection", "Hand-picked plants to match your vision", "Artisan decorative elements", "Personal delivery and in-home setup"] },
        { title: "Exclusive Benefits", items: ["Lifetime care support hotline", "Professional photography of your terrarium", "Priority access to future releases", "Personal thank you video from Ryan"] },
      ],
    },
  ];

  const handleContinue = () => {
    const tier = tiers.find((t) => t.id === selectedTier);
    if (tier) {
      updateState({
        terrariumStyle: checkoutState.biome,
        terrariumStyleName: `${tier.name} ${biomeName}`,
        terrariumPrice: tier.price,
        hasPremiumPlants: selectedTier === "premium" || selectedTier === "custom",
        hasCustomDesign: selectedTier === "custom",
        wantsInsurance: true,
        step: selectedTier === "base" ? 6 : 8,
      });
    }
  };

  const selectedTierData = tiers.find((t) => t.id === selectedTier);

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-start py-8 px-4 bg-background">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-8 max-w-2xl">
        <ShoppingBag className="w-12 h-12 text-primary mx-auto mb-4" />
        <h1 className="font-serif text-3xl md:text-4xl font-semibold mb-3">Choose Your Experience</h1>
        <p className="text-muted-foreground text-lg">Select the perfect tier for your {biomeName} terrarium</p>
      </motion.div>

      <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
        {tiers.map((tier, index) => (
          <motion.div key={tier.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} className="relative" onMouseEnter={() => setHoveredTier(tier.id)} onMouseLeave={() => setHoveredTier(null)}>
            {tier.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: "spring", stiffness: 500 }} className="bg-primary text-primary-foreground text-xs font-semibold px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                  <Settings2 className="w-3.5 h-3.5" />
                  Most Popular
                </motion.div>
              </div>
            )}

            <Card
              className={`relative overflow-hidden cursor-pointer transition-all duration-300 h-full border-2 ${selectedTier === tier.id ? "ring-2 ring-primary ring-offset-2 ring-offset-background border-primary shadow-xl" : tier.borderColor} ${hoveredTier === tier.id && selectedTier !== tier.id ? "shadow-lg scale-[1.02]" : ""} ${tier.popular ? "md:-mt-2 md:mb-2" : ""}`}
              onClick={() => setSelectedTier(tier.id)}
              data-testid={`tier-card-${tier.id}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br opacity-50 transition-opacity duration-300 ${tier.gradientFrom} ${tier.gradientTo} ${hoveredTier === tier.id ? "opacity-100" : ""}`} />

              <AnimatePresence>
                {selectedTier === tier.id && (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} className="absolute top-4 right-4 w-7 h-7 rounded-full bg-primary flex items-center justify-center z-10">
                    <Check className="w-4 h-4 text-primary-foreground" />
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="relative p-6 flex flex-col h-full">
                <div className="text-center mb-4">
                  <div className={`w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center transition-transform duration-300 bg-gradient-to-br ${tier.gradientFrom} ${tier.gradientTo} ${tier.color} ${hoveredTier === tier.id ? "scale-110" : ""}`}>
                    {tier.icon}
                  </div>
                  <h3 className={`font-serif text-2xl font-semibold ${tier.color}`}>{tier.name}</h3>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mt-1">{tier.tagline}</p>
                </div>

                <div className="text-center mb-4">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-2xl font-medium text-muted-foreground">$</span>
                    <span className="font-serif text-5xl font-bold">{tier.price.toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">One-time purchase</p>
                </div>

                <p className="text-center text-sm text-muted-foreground mb-5 min-h-[40px]">{tier.description}</p>

                <ul className="space-y-2.5 flex-grow mb-4">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className={`flex items-center gap-2 text-sm ${feature.included ? "text-foreground" : "text-muted-foreground/50 line-through"}`}>
                      {feature.included ? <Check className={`w-4 h-4 flex-shrink-0 ${tier.color}`} /> : <div className="w-4 h-4 flex-shrink-0" />}
                      <span className="flex-grow">{feature.text}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${showBreakdown === tier.id ? `${tier.color} bg-current/10` : "text-muted-foreground hover:bg-muted"}`}
                  onClick={(e) => { e.stopPropagation(); setShowBreakdown(showBreakdown === tier.id ? null : tier.id); }}
                  data-testid={`tier-breakdown-${tier.id}`}
                >
                  {showBreakdown === tier.id ? "Hide Details" : "View Details"}
                </button>

                <AnimatePresence>
                  {showBreakdown === tier.id && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                      <div className="pt-4 mt-4 border-t border-border/50">
                        {tier.detailedBreakdown.map((section, sectionIndex) => (
                          <div key={sectionIndex} className="mb-4 last:mb-0">
                            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{section.title}</h4>
                            <ul className="space-y-1.5">
                              {section.items.map((item, itemIndex) => (
                                <li key={itemIndex} className="flex items-start gap-2 text-xs text-foreground/80">
                                  <Sparkles className={`w-3 h-3 flex-shrink-0 mt-0.5 ${tier.color}`} />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }} className="w-full max-w-2xl mb-8">
        <Card className="p-5 bg-muted/30">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedTierData?.color} bg-gradient-to-br ${selectedTierData?.gradientFrom} ${selectedTierData?.gradientTo}`}>
                {selectedTierData?.icon}
              </div>
              <div>
                <p className="font-medium">{selectedTierData?.name} {biomeName} Terrarium</p>
                <p className="text-sm text-muted-foreground">{selectedTierData?.tagline}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-serif text-3xl font-bold text-primary">${selectedTierData?.price.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">One-time purchase</p>
            </div>
          </div>
        </Card>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="flex flex-wrap justify-center gap-3 mb-8">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-muted/50 rounded-full text-xs text-muted-foreground">
          <Shield className="w-3.5 h-3.5" />
          <span>Base: 30-day | Premium: 1-year | Custom: Lifetime</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-muted/50 rounded-full text-xs text-muted-foreground">
          <Gift className="w-3.5 h-3.5" />
          <span>Base: 6" | Premium: 8" | Custom: Custom size</span>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="flex gap-4 w-full max-w-md">
        <Button variant="outline" onClick={() => goToStep(4)} className="flex-1" data-testid="button-back">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button onClick={handleContinue} className="flex-1" size="lg" data-testid="button-continue">
          Continue with {selectedTierData?.name}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="mt-8 flex flex-wrap justify-center gap-6 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <Shield className="w-4 h-4" />
          <span>Secure Payment</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Truck className="w-4 h-4" />
          <span>Handcrafted in Colorado</span>
        </div>
        <div className="flex items-center gap-1.5">
          <MessageCircle className="w-4 h-4" />
          <span>Personal Support</span>
        </div>
      </motion.div>
    </div>
  );
}

// Step 6: Upgrades (Upsell checkboxes)
function Step6Upgrades({
  checkoutState,
  updateState,
  goToStep,
  handleNext,
}: {
  checkoutState: CheckoutState;
  updateState: (updates: Partial<CheckoutState>) => void;
  goToStep: (step: number) => void;
  handleNext: (fromStep: number) => void;
}) {
  const [hasPremiumPlants, setHasPremiumPlants] = useState(checkoutState.hasPremiumPlants);
  const [hasCustomDesign, setHasCustomDesign] = useState(checkoutState.hasCustomDesign);

  const isExperience = checkoutState.terrariumStyle === "experience-2" || checkoutState.terrariumStyle === "experience-4";

  // Auto-skip for experience packages
  useEffect(() => {
    if (isExperience) {
      handleNext(6);
    }
  }, [isExperience]);

  const handleContinue = () => {
    // Calculate next step based on LOCAL state (not checkoutState which hasn't updated yet)
    const hasAnyUpsell = hasPremiumPlants || hasCustomDesign;
    const nextStep = hasAnyUpsell ? 7 : 8; // Go to Experience (7) if any upsell, otherwise skip to Gift (8)
    updateState({ hasPremiumPlants, hasCustomDesign, step: nextStep });
  };

  if (isExperience) {
    return null;
  }

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-3xl"
      >
        <div className="text-center mb-8">
          <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-serif text-3xl md:text-4xl font-semibold mb-3">Enhance Your Terrarium</h1>
          <p className="text-muted-foreground">Optional upgrades to make it truly special</p>
          <p className="text-sm text-muted-foreground/70 mt-2">Tap to select one or both</p>
        </div>

        {/* Beautiful Image Cards Grid - 2-up on all screens */}
        <div className="grid grid-cols-2 gap-3 md:gap-4 mb-8">
          {/* Premium Rare Plants Card */}
          <div
            className={`relative aspect-[4/5] rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
              hasPremiumPlants
                ? "ring-3 ring-primary ring-offset-2 ring-offset-background scale-[1.02]"
                : "hover:scale-[1.01]"
            }`}
            onClick={() => setHasPremiumPlants(!hasPremiumPlants)}
            data-testid="upgrade-premium-plants"
          >
            <OptimizedImage
              src={craftingProcess}
              alt="Premium Rare Plants"
              fill
              objectFit="cover"
              rootMargin="300px"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10 z-10" />

            {hasPremiumPlants && (
              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-lg z-20">
                <Check className="w-5 h-5 text-primary-foreground" />
              </div>
            )}

            {/* Price Badge */}
            <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-emerald-500/90 backdrop-blur-sm z-20">
              <span className="font-serif text-sm font-bold text-white">+$499</span>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-5 text-white z-20">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-emerald-300" />
                <h3 className="font-serif text-xl font-semibold">Premium Rare Plants</h3>
              </div>
              <p className="text-sm text-white/80 leading-relaxed">
                Exotic, rare plant varieties curated by Ryan. Hard-to-find species that make your terrarium truly unique.
              </p>
              <ul className="mt-3 space-y-1">
                <li className="flex items-center gap-2 text-xs text-white/70">
                  <Check className="w-3 h-3 text-emerald-400" />
                  <span>Hand-selected rare specimens</span>
                </li>
                <li className="flex items-center gap-2 text-xs text-white/70">
                  <Check className="w-3 h-3 text-emerald-400" />
                  <span>Collector-grade quality</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Custom Design with Ryan Card */}
          <div
            className={`relative aspect-[4/5] rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
              hasCustomDesign
                ? "ring-3 ring-primary ring-offset-2 ring-offset-background scale-[1.02]"
                : "hover:scale-[1.01]"
            }`}
            onClick={() => setHasCustomDesign(!hasCustomDesign)}
            data-testid="upgrade-custom-design"
          >
            <OptimizedImage
              src={ryanArtisan}
              alt="Custom Design with Ryan"
              fill
              objectFit="cover"
              rootMargin="300px"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10 z-10" />

            {hasCustomDesign && (
              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-lg z-20">
                <Check className="w-5 h-5 text-primary-foreground" />
              </div>
            )}

            {/* Price Badge */}
            <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-violet-500/90 backdrop-blur-sm z-20">
              <span className="font-serif text-sm font-bold text-white">+$999</span>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-5 text-white z-20">
              <div className="flex items-center gap-2 mb-2">
                <MessageCircle className="w-5 h-5 text-violet-300" />
                <h3 className="font-serif text-xl font-semibold">Custom Design with Ryan</h3>
              </div>
              <p className="text-sm text-white/80 leading-relaxed">
                15-minute consultation to design your perfect terrarium. Share your vision and Ryan brings it to life.
              </p>
              <ul className="mt-3 space-y-1">
                <li className="flex items-center gap-2 text-xs text-white/70">
                  <Check className="w-3 h-3 text-violet-400" />
                  <span>1:1 personal consultation</span>
                </li>
                <li className="flex items-center gap-2 text-xs text-white/70">
                  <Check className="w-3 h-3 text-violet-400" />
                  <span>Fully bespoke design</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Running Total */}
        <Card className="p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-muted-foreground">Your total</p>
              <p className="text-sm">
                $499 base
                {(hasPremiumPlants || hasCustomDesign) && (
                  <span className="text-muted-foreground">
                    {hasPremiumPlants && " + $499"}
                    {hasCustomDesign && " + $999"}
                  </span>
                )}
              </p>
            </div>
            <p className="font-serif text-2xl font-bold text-primary">
              ${(499 + (hasPremiumPlants ? 499 : 0) + (hasCustomDesign ? 999 : 0)).toLocaleString()}
            </p>
          </div>
        </Card>

        <p className="text-center text-sm text-muted-foreground mb-4">
          These upgrades are optional. Hit "continue" to continue with your base package.
        </p>

        <div className="flex gap-4 max-w-md mx-auto">
          <Button variant="outline" onClick={() => goToStep(5)} className="flex-1" data-testid="button-back">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleContinue} className="flex-1" data-testid="button-continue">
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

// Step 7: Experience Selection (Conditional - only if hasAnyUpsell)
function Step7Experience({
  checkoutState,
  updateState,
  goToStep,
  handleNext,
  handleBack,
}: {
  checkoutState: CheckoutState;
  updateState: (updates: Partial<CheckoutState>) => void;
  goToStep: (step: number) => void;
  handleNext: (fromStep: number) => void;
  handleBack: (fromStep: number) => void;
}) {
  const [experienceType, setExperienceType] = useState<'none' | '2-person' | '4-person'>(checkoutState.experienceType);

  const handleContinue = () => {
    updateState({ experienceType });
    handleNext(7);
  };

  // Pricing constants
  const baseTerrarium = 499;
  const duoExperienceTotal = 2000;
  const groupExperienceTotal = 4000;

  // Calculate deltas (experience price minus what they'd pay for terrarium)
  const duoExperienceDelta = duoExperienceTotal - baseTerrarium; // +$1,501
  const groupExperienceDelta = groupExperienceTotal - baseTerrarium; // +$3,501

  // Calculate current total for display
  let currentTotal = baseTerrarium;
  if (checkoutState.hasPremiumPlants) currentTotal += 499;
  if (checkoutState.hasCustomDesign) currentTotal += 999;

  let experienceAddOn = 0;
  if (experienceType === '2-person') {
    experienceAddOn = duoExperienceDelta;
  } else if (experienceType === '4-person') {
    experienceAddOn = groupExperienceDelta;
  }

  const grandTotal = currentTotal + experienceAddOn;

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-4xl"
      >
        <div className="text-center mb-8">
          <Mountain className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-serif text-3xl md:text-4xl font-semibold mb-3">Make It an Experience</h1>
          <p className="text-muted-foreground">Turn your custom terrarium into a full-day nature adventure</p>
        </div>

        {/* Beautiful Image Cards Grid - 2-up on mobile, 3-up on desktop */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-8">
          {/* 2-Person Nature Experience */}
          <div
            className={`relative aspect-[3/4] rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
              experienceType === '2-person'
                ? "ring-3 ring-primary ring-offset-2 ring-offset-background scale-[1.02]"
                : "hover:scale-[1.01]"
            }`}
            onClick={() => setExperienceType('2-person')}
            data-testid="experience-2-person"
          >
            <OptimizedImage
              src={heroImage}
              alt="2-Person Nature Experience"
              fill
              objectFit="cover"
              rootMargin="300px"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10 z-10" />

            {experienceType === '2-person' && (
              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-lg z-20">
                <Check className="w-5 h-5 text-primary-foreground" />
              </div>
            )}

            {/* Price Badge */}
            <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-amber-500/90 backdrop-blur-sm z-20">
              <span className="font-serif text-sm font-bold text-white">+$1,501</span>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-4 text-white z-20">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-amber-300" />
                <h3 className="font-serif text-lg font-semibold">2-Person Experience</h3>
              </div>
              <p className="text-xs text-white/80 leading-relaxed mb-2">
                Morning hike in Colorado wilderness, gather materials, afternoon workshop with Ryan
              </p>
              <p className="text-xs text-amber-200 italic">
                Your $499 terrarium included
              </p>
            </div>
          </div>

          {/* 4-Person Nature Experience */}
          <div
            className={`relative aspect-[3/4] rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
              experienceType === '4-person'
                ? "ring-3 ring-primary ring-offset-2 ring-offset-background scale-[1.02]"
                : "hover:scale-[1.01]"
            }`}
            onClick={() => setExperienceType('4-person')}
            data-testid="experience-4-person"
          >
            <OptimizedImage
              src={craftingProcess}
              alt="4-Person Nature Experience"
              fill
              objectFit="cover"
              rootMargin="300px"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10 z-10" />

            {experienceType === '4-person' && (
              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-lg z-20">
                <Check className="w-5 h-5 text-primary-foreground" />
              </div>
            )}

            {/* Price Badge */}
            <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-rose-500/90 backdrop-blur-sm z-20">
              <span className="font-serif text-sm font-bold text-white">+$3,501</span>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-4 text-white z-20">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-rose-300" />
                <h3 className="font-serif text-lg font-semibold">4-Person Experience</h3>
              </div>
              <p className="text-xs text-white/80 leading-relaxed mb-2">
                Bring friends! Full day adventure for up to 4 people
              </p>
              <p className="text-xs text-rose-200 italic">
                Your $499 terrarium included
              </p>
            </div>
          </div>

          {/* Just the Terrarium */}
          <div
            className={`relative aspect-[3/4] rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
              experienceType === 'none'
                ? "ring-3 ring-primary ring-offset-2 ring-offset-background scale-[1.02]"
                : "hover:scale-[1.01]"
            }`}
            onClick={() => setExperienceType('none')}
            data-testid="experience-none"
          >
            <OptimizedImage
              src={ryanArtisan}
              alt="Custom Terrarium Only"
              fill
              objectFit="cover"
              rootMargin="300px"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10 z-10" />

            {experienceType === 'none' && (
              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-lg z-20">
                <Check className="w-5 h-5 text-primary-foreground" />
              </div>
            )}

            {/* No extra cost badge */}
            <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-slate-500/90 backdrop-blur-sm z-20">
              <span className="font-serif text-sm font-bold text-white">No extra</span>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-4 text-white z-20">
              <div className="flex items-center gap-2 mb-2">
                <Leaf className="w-5 h-5 text-emerald-300" />
                <h3 className="font-serif text-lg font-semibold">Just the Terrarium</h3>
              </div>
              <p className="text-xs text-white/80 leading-relaxed">
                Continue with your custom design only
              </p>
            </div>
          </div>
        </div>

        {/* Running Total */}
        <Card className="p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-muted-foreground">Your total</p>
              <p className="text-sm">
                ${currentTotal.toLocaleString()} base
                {experienceType !== 'none' && (
                  <span className="text-muted-foreground">
                    {experienceType === '2-person' && " + $1,501"}
                    {experienceType === '4-person' && " + $3,501"}
                  </span>
                )}
              </p>
            </div>
            <p className="font-serif text-2xl font-bold text-primary">
              ${grandTotal.toLocaleString()}
            </p>
          </div>
        </Card>

        <div className="flex gap-4 max-w-md mx-auto">
          <Button variant="outline" onClick={() => handleBack(7)} className="flex-1" data-testid="button-back">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleContinue} className="flex-1" data-testid="button-continue">
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

// Step 2: Delivery Week (Calendar with available weeks - 2 max per week)
function Step2Delivery({
  checkoutState,
  updateState,
  goToStep,
  blockedWeeks,
}: {
  checkoutState: CheckoutState;
  updateState: (updates: Partial<CheckoutState>) => void;
  goToStep: (step: number) => void;
  blockedWeeks: string[];
}) {
  const [selectedWeek, setSelectedWeek] = useState(checkoutState.deliveryWeek);
  const [error, setError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<Record<string, number>>({});
  const [loadingAvailability, setLoadingAvailability] = useState(true);
  const [showTimeline, setShowTimeline] = useState(false);

  const allWeeks = getAvailableWeeks();
  const availableWeeks = allWeeks.filter((week) => !blockedWeeks.includes(week.value));

  // Fetch availability on mount
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const weeks = availableWeeks.map(w => w.value);
        const response = await fetch('/api/week-availability', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ weeks }),
        });
        if (response.ok) {
          const data = await response.json();
          setAvailability(data);
        }
      } catch (err) {
        console.error('Failed to fetch availability:', err);
      } finally {
        setLoadingAvailability(false);
      }
    };
    fetchAvailability();
  }, []);

  // Find the soonest available week (not sold out)
  const soonestAvailableWeek = availableWeeks.find((week) => {
    const remaining = availability[week.value];
    return remaining === undefined || remaining > 0;
  });

  // Auto-select soonest available if nothing selected
  useEffect(() => {
    if (!loadingAvailability && !selectedWeek && soonestAvailableWeek) {
      setSelectedWeek(soonestAvailableWeek.value);
    }
  }, [loadingAvailability, selectedWeek, soonestAvailableWeek]);

  const handleContinue = () => {
    if (!selectedWeek) {
      setError("Please select a delivery week");
      return;
    }
    updateState({ deliveryWeek: selectedWeek, step: 3 });
  };

  const getAvailabilityStatus = (weekValue: string): 'available' | 'limited' | 'full' => {
    const remaining = availability[weekValue];
    if (remaining === undefined) return 'available';
    if (remaining === 0) return 'full';
    if (remaining === 1) return 'limited';
    return 'available';
  };

  const getAvailabilityBadge = (weekValue: string) => {
    const status = getAvailabilityStatus(weekValue);

    if (status === 'full') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
          Full
        </span>
      );
    } else if (status === 'limited') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          1 Spot Left
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Available
        </span>
      );
    }
  };

  const isWeekSoldOut = (weekValue: string) => {
    return availability[weekValue] === 0;
  };

  // Timeline steps for terrarium creation process
  const timelineSteps = [
    { icon: Calendar, label: "Week Reserved", description: "Your delivery week is locked in" },
    { icon: Leaf, label: "Plants Sourced", description: "Ryan selects the perfect specimens" },
    { icon: Wrench, label: "Handcrafted", description: "Your terrarium is built with care" },
    { icon: Truck, label: "Delivered", description: "Hand-delivered to your door" },
  ];

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-3xl"
      >
        <div className="text-center mb-6">
          <Calendar className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-serif text-3xl md:text-4xl font-semibold mb-3">Reserve Your Delivery Week</h1>
          <p className="text-muted-foreground">Ryan crafts only 1-2 terrariums per week for artisan quality</p>
        </div>

        {/* Creation Process Timeline */}
        <div className="mb-8">
          <button
            onClick={() => setShowTimeline(!showTimeline)}
            className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <Sparkles className="w-4 h-4" />
            <span>See the creation process</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showTimeline ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {showTimeline && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="bg-muted/30 rounded-xl p-4 sm:p-6 mb-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-2">
                    {timelineSteps.map((step, index) => (
                      <div key={step.label} className="flex sm:flex-col items-center gap-3 sm:gap-2 flex-1">
                        <div className="relative">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${
                            index === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted-foreground/20 text-muted-foreground'
                          }`}>
                            <step.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                          </div>
                          {index < timelineSteps.length - 1 && (
                            <div className="hidden sm:block absolute top-1/2 left-full w-full h-0.5 bg-muted-foreground/20" style={{ transform: 'translateY(-50%)', width: 'calc(100% + 1rem)' }} />
                          )}
                        </div>
                        <div className="text-left sm:text-center">
                          <p className="text-xs sm:text-sm font-medium">{step.label}</p>
                          <p className="text-[10px] sm:text-xs text-muted-foreground hidden sm:block">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Week Cards Grid */}
        <div className="mb-6">
          {loadingAvailability ? (
            <div className="text-center py-8 text-muted-foreground">
              <div className="animate-pulse flex flex-col items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-muted-foreground/20" />
                <p>Checking availability...</p>
              </div>
            </div>
          ) : availableWeeks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No delivery weeks currently available.</p>
              <p className="text-sm mt-2">Please check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {availableWeeks.slice(0, 8).map((week) => {
                const soldOut = isWeekSoldOut(week.value);
                const isSoonest = soonestAvailableWeek?.value === week.value;
                const status = getAvailabilityStatus(week.value);

                return (
                  <motion.div
                    key={week.value}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                    className={`relative p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 ${
                      soldOut
                        ? "border-border bg-muted/30 cursor-not-allowed opacity-50 grayscale"
                        : selectedWeek === week.value
                        ? "border-primary bg-primary/10 ring-2 ring-primary ring-offset-2 ring-offset-background cursor-pointer shadow-lg"
                        : isSoonest
                        ? "border-emerald-500/50 bg-emerald-50/50 dark:bg-emerald-900/10 hover:border-emerald-500 cursor-pointer"
                        : status === 'limited'
                        ? "border-amber-300/50 hover:border-amber-400 hover:bg-amber-50/30 dark:hover:bg-amber-900/10 cursor-pointer"
                        : "border-border hover:border-primary/50 hover:bg-muted/50 cursor-pointer"
                    }`}
                    onClick={() => {
                      if (!soldOut) {
                        setSelectedWeek(week.value);
                        setError(null);
                      }
                    }}
                    data-testid={`week-option-${week.value}`}
                  >
                    {/* Soonest Available Badge */}
                    {isSoonest && !soldOut && selectedWeek !== week.value && (
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500 text-white shadow-sm">
                          <Sparkles className="w-3 h-3" />
                          Soonest
                        </span>
                      </div>
                    )}

                    {/* Selected Checkmark */}
                    {selectedWeek === week.value && !soldOut && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center shadow-sm">
                        <Check className="w-4 h-4 text-primary-foreground" />
                      </div>
                    )}

                    <div className="text-center">
                      <h3 className={`font-serif text-base sm:text-lg font-semibold mb-1 ${
                        selectedWeek === week.value ? 'pr-0' : 'pr-6 sm:pr-0'
                      } ${soldOut ? 'line-through' : ''}`}>
                        {week.weekLabel}
                      </h3>
                      <p className={`text-xs sm:text-sm text-muted-foreground mb-3 ${soldOut ? 'line-through' : ''}`}>
                        {week.label}
                      </p>
                      <div className="mt-2">
                        {getAvailabilityBadge(week.value)}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Capacity Legend */}
        <div className="text-center mb-6">
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500" /> Available
            </span>
            <span className="mx-2 hidden sm:inline">|</span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-500" /> Limited
            </span>
            <span className="mx-2 hidden sm:inline">|</span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500" /> Full
            </span>
          </p>
        </div>

        {error && <p className="text-sm text-destructive text-center mb-4">{error}</p>}

        <div className="flex gap-4 max-w-md mx-auto">
          <Button variant="outline" onClick={() => goToStep(1)} className="flex-1" data-testid="button-back">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleContinue}
            className="flex-1"
            disabled={!selectedWeek || isWeekSoldOut(selectedWeek)}
            data-testid="button-continue"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
// Gift wrapping options
const GIFT_WRAPPING_OPTIONS = [
  { value: 'standard', label: 'Standard Gift Wrap', description: 'Eco-friendly kraft paper with ribbon', price: 0 },
  { value: 'premium', label: 'Premium Gift Wrap', description: 'Luxury box with custom tissue paper and bow', price: 15 },
  { value: 'none', label: 'No Wrapping', description: 'Ship in standard packaging', price: 0 },
] as const;

// Get available preferred delivery dates (next 8 weeks, excluding past dates)
function getAvailableDeliveryDates(): { value: string; label: string }[] {
  const dates: { value: string; label: string }[] = [];
  const today = new Date();

  // Start from 2 weeks out (minimum lead time)
  for (let i = 14; i < 70; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);

    dates.push({
      value: date.toISOString().split("T")[0],
      label: date.toLocaleDateString("en-US", { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
    });
  }

  return dates;
}

// Step 8: Gift Options - Complete Gift Order Flow
function Step8Gift({
  checkoutState,
  updateState,
  goToStep,
  handleBack,
}: {
  checkoutState: CheckoutState;
  updateState: (updates: Partial<CheckoutState>) => void;
  goToStep: (step: number) => void;
  handleBack: (fromStep: number) => void;
}) {
  // Core gift toggle
  const [isGift, setIsGift] = useState(checkoutState.isGift);

  // Recipient information
  const [recipientName, setRecipientName] = useState(checkoutState.recipientName);
  const [recipientEmail, setRecipientEmail] = useState(checkoutState.recipientEmail);
  const [occasion, setOccasion] = useState(checkoutState.occasion);

  // Gift message with character limit
  const [giftMessage, setGiftMessage] = useState(checkoutState.giftMessage);
  const MAX_MESSAGE_LENGTH = 200;

  // Delivery address options
  const [useDifferentDeliveryAddress, setUseDifferentDeliveryAddress] = useState(checkoutState.useDifferentDeliveryAddress);
  const [giftDeliveryAddress, setGiftDeliveryAddress] = useState(checkoutState.giftDeliveryAddress);
  const [giftDeliveryCity, setGiftDeliveryCity] = useState(checkoutState.giftDeliveryCity);
  const [giftDeliveryState, setGiftDeliveryState] = useState(checkoutState.giftDeliveryState);
  const [giftDeliveryZip, setGiftDeliveryZip] = useState(checkoutState.giftDeliveryZip);

  // Preferred delivery date
  const [preferredDeliveryDate, setPreferredDeliveryDate] = useState(checkoutState.preferredDeliveryDate);

  // Notification preferences
  const [notifyOnShip, setNotifyOnShip] = useState(checkoutState.notifyOnShip);

  // Gift wrapping
  const [giftWrapping, setGiftWrapping] = useState<'standard' | 'premium' | 'none'>(checkoutState.giftWrapping);

  // Gift receipt (hide pricing)
  const [hideGiftReceipt, setHideGiftReceipt] = useState(checkoutState.hideGiftReceipt);

  // Validation errors
  const [errors, setErrors] = useState<{
    recipientName?: string;
    recipientEmail?: string;
    giftDeliveryAddress?: string;
    giftDeliveryCity?: string;
    giftDeliveryState?: string;
    giftDeliveryZip?: string;
  }>({});

  const availableDeliveryDates = getAvailableDeliveryDates();

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (isGift) {
      if (!recipientName || recipientName.length < 2) {
        newErrors.recipientName = "Please enter the recipient's name";
      }

      if (recipientEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
        newErrors.recipientEmail = "Please enter a valid email address";
      }

      if (useDifferentDeliveryAddress) {
        if (!giftDeliveryAddress) {
          newErrors.giftDeliveryAddress = "Please enter a delivery address";
        }
        if (!giftDeliveryCity) {
          newErrors.giftDeliveryCity = "Please enter a city";
        }
        if (!giftDeliveryState) {
          newErrors.giftDeliveryState = "Please select a state";
        }
        if (!giftDeliveryZip || !/^\d{5}(-\d{4})?$/.test(giftDeliveryZip)) {
          newErrors.giftDeliveryZip = "Please enter a valid ZIP code";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_MESSAGE_LENGTH) {
      setGiftMessage(value);
    }
  };

  const handleContinue = () => {
    if (!validateForm()) {
      return;
    }

    updateState({
      isGift,
      recipientName: isGift ? recipientName : "",
      recipientEmail: isGift ? recipientEmail : "",
      occasion: isGift ? occasion : "",
      giftMessage: isGift ? giftMessage : "",
      useDifferentDeliveryAddress: isGift ? useDifferentDeliveryAddress : false,
      giftDeliveryAddress: isGift && useDifferentDeliveryAddress ? giftDeliveryAddress : "",
      giftDeliveryCity: isGift && useDifferentDeliveryAddress ? giftDeliveryCity : "",
      giftDeliveryState: isGift && useDifferentDeliveryAddress ? giftDeliveryState : "",
      giftDeliveryZip: isGift && useDifferentDeliveryAddress ? giftDeliveryZip : "",
      preferredDeliveryDate: isGift ? preferredDeliveryDate : "",
      notifyOnShip: isGift ? notifyOnShip : true,
      giftWrapping: isGift ? giftWrapping : "standard",
      hideGiftReceipt: isGift ? hideGiftReceipt : true,
      step: 9,
    });
  };

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl"
      >
        <div className="text-center mb-8">
          <Gift className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-serif text-3xl md:text-4xl font-semibold mb-3">Gift Options</h1>
          <p className="text-muted-foreground">Make it extra special for someone you care about</p>
        </div>

        <Card className="p-6 md:p-8">
          {/* Gift Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 mb-6">
            <div>
              <p className="font-medium">Is this a gift?</p>
              <p className="text-sm text-muted-foreground">We'll include a personalized gift card and special packaging</p>
            </div>
            <Switch checked={isGift} onCheckedChange={setIsGift} data-testid="switch-is-gift" />
          </div>

          <AnimatePresence>
            {isGift && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-6 overflow-hidden"
              >
                {/* Recipient Information Section */}
                <div className="space-y-4">
                  <h3 className="font-medium text-lg border-b pb-2">Recipient Information</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="recipientName">Recipient's Name *</Label>
                      <Input
                        id="recipientName"
                        placeholder="Who is this gift for?"
                        value={recipientName}
                        onChange={(e) => {
                          setRecipientName(e.target.value);
                          setErrors((prev) => ({ ...prev, recipientName: undefined }));
                        }}
                        className={errors.recipientName ? "border-destructive" : ""}
                        data-testid="input-recipient-name"
                      />
                      {errors.recipientName && <p className="text-sm text-destructive">{errors.recipientName}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="recipientEmail">Recipient's Email (optional)</Label>
                      <Input
                        id="recipientEmail"
                        type="email"
                        placeholder="recipient@example.com"
                        value={recipientEmail}
                        onChange={(e) => {
                          setRecipientEmail(e.target.value);
                          setErrors((prev) => ({ ...prev, recipientEmail: undefined }));
                        }}
                        className={errors.recipientEmail ? "border-destructive" : ""}
                        data-testid="input-recipient-email"
                      />
                      {errors.recipientEmail && <p className="text-sm text-destructive">{errors.recipientEmail}</p>}
                      <p className="text-xs text-muted-foreground">We'll send them care instructions after delivery</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="occasion">Special Occasion</Label>
                    <Select value={occasion} onValueChange={setOccasion}>
                      <SelectTrigger data-testid="select-occasion">
                        <SelectValue placeholder="Select an occasion (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {OCCASIONS.map((occ) => (
                          <SelectItem key={occ.value} value={occ.value}>
                            {occ.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Gift Message Section */}
                <div className="space-y-4">
                  <h3 className="font-medium text-lg border-b pb-2">Personal Message</h3>

                  <div className="space-y-2">
                    <Label htmlFor="giftMessage">Gift Card Message</Label>
                    <Textarea
                      id="giftMessage"
                      placeholder="Write a personal message for the gift card..."
                      value={giftMessage}
                      onChange={handleMessageChange}
                      className="min-h-[120px]"
                      data-testid="textarea-gift-message"
                    />
                    <div className="flex justify-between items-center">
                      <p className={`text-xs ${giftMessage.length >= MAX_MESSAGE_LENGTH ? 'text-destructive' : 'text-muted-foreground'}`}>
                        {giftMessage.length}/{MAX_MESSAGE_LENGTH} characters
                      </p>
                    </div>
                  </div>

                  {/* Message Preview */}
                  {giftMessage && (
                    <div className="p-4 rounded-lg bg-muted/30 border border-dashed">
                      <p className="text-xs text-muted-foreground mb-2">Preview of your gift card:</p>
                      <div className="p-4 bg-background rounded shadow-sm">
                        <p className="text-sm font-medium mb-1">To: {recipientName || "Recipient"}</p>
                        <p className="text-sm italic">"{giftMessage}"</p>
                        <p className="text-sm text-muted-foreground mt-2">- From {checkoutState.name || "A Friend"}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Delivery Address Section */}
                <div className="space-y-4">
                  <h3 className="font-medium text-lg border-b pb-2">Delivery Address</h3>

                  <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/30">
                    <Checkbox
                      id="differentAddress"
                      checked={useDifferentDeliveryAddress}
                      onCheckedChange={(checked) => setUseDifferentDeliveryAddress(checked === true)}
                      data-testid="checkbox-different-address"
                    />
                    <Label htmlFor="differentAddress" className="text-sm cursor-pointer">
                      Ship to a different address than my billing address
                    </Label>
                  </div>

                  <AnimatePresence>
                    {useDifferentDeliveryAddress && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4 overflow-hidden"
                      >
                        <div className="space-y-2">
                          <Label htmlFor="giftDeliveryAddress">Street Address *</Label>
                          <Input
                            id="giftDeliveryAddress"
                            placeholder="123 Main St, Apt 4"
                            value={giftDeliveryAddress}
                            onChange={(e) => {
                              setGiftDeliveryAddress(e.target.value);
                              setErrors((prev) => ({ ...prev, giftDeliveryAddress: undefined }));
                            }}
                            className={errors.giftDeliveryAddress ? "border-destructive" : ""}
                            data-testid="input-gift-address"
                          />
                          {errors.giftDeliveryAddress && <p className="text-sm text-destructive">{errors.giftDeliveryAddress}</p>}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="col-span-2 space-y-2">
                            <Label htmlFor="giftDeliveryCity">City *</Label>
                            <Input
                              id="giftDeliveryCity"
                              placeholder="Denver"
                              value={giftDeliveryCity}
                              onChange={(e) => {
                                setGiftDeliveryCity(e.target.value);
                                setErrors((prev) => ({ ...prev, giftDeliveryCity: undefined }));
                              }}
                              className={errors.giftDeliveryCity ? "border-destructive" : ""}
                              data-testid="input-gift-city"
                            />
                            {errors.giftDeliveryCity && <p className="text-sm text-destructive">{errors.giftDeliveryCity}</p>}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="giftDeliveryState">State *</Label>
                            <Select value={giftDeliveryState} onValueChange={(v) => {
                              setGiftDeliveryState(v);
                              setErrors((prev) => ({ ...prev, giftDeliveryState: undefined }));
                            }}>
                              <SelectTrigger data-testid="select-gift-state" className={errors.giftDeliveryState ? "border-destructive" : ""}>
                                <SelectValue placeholder="State" />
                              </SelectTrigger>
                              <SelectContent>
                                {US_STATES.map((state) => (
                                  <SelectItem key={state.value} value={state.value}>
                                    {state.value}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {errors.giftDeliveryState && <p className="text-sm text-destructive">{errors.giftDeliveryState}</p>}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="giftDeliveryZip">ZIP Code *</Label>
                            <Input
                              id="giftDeliveryZip"
                              placeholder="80202"
                              value={giftDeliveryZip}
                              onChange={(e) => {
                                setGiftDeliveryZip(e.target.value);
                                setErrors((prev) => ({ ...prev, giftDeliveryZip: undefined }));
                              }}
                              className={errors.giftDeliveryZip ? "border-destructive" : ""}
                              data-testid="input-gift-zip"
                            />
                            {errors.giftDeliveryZip && <p className="text-sm text-destructive">{errors.giftDeliveryZip}</p>}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Preferred Delivery Date */}
                <div className="space-y-4">
                  <h3 className="font-medium text-lg border-b pb-2">Delivery Preferences</h3>

                  <div className="space-y-2">
                    <Label htmlFor="preferredDeliveryDate">Preferred Delivery Date (optional)</Label>
                    <Select value={preferredDeliveryDate} onValueChange={setPreferredDeliveryDate}>
                      <SelectTrigger data-testid="select-delivery-date">
                        <SelectValue placeholder="Select a preferred date (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDeliveryDates.slice(0, 21).map((date) => (
                          <SelectItem key={date.value} value={date.value}>
                            {date.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">We'll do our best to deliver on or before this date</p>
                  </div>
                </div>

                {/* Notification Preferences */}
                <div className="space-y-4">
                  <h3 className="font-medium text-lg border-b pb-2">Notification Preferences</h3>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div>
                      <p className="font-medium">Notify me when the gift ships</p>
                      <p className="text-sm text-muted-foreground">
                        {notifyOnShip
                          ? "You'll receive tracking information when shipped"
                          : "Keep it a surprise - no shipping notifications"}
                      </p>
                    </div>
                    <Switch
                      checked={notifyOnShip}
                      onCheckedChange={setNotifyOnShip}
                      data-testid="switch-notify-on-ship"
                    />
                  </div>

                  {!notifyOnShip && (
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                      <p className="font-medium">Surprise mode enabled!</p>
                      <p>You won't receive shipping updates. The recipient will get a delivery notification instead.</p>
                    </div>
                  )}
                </div>

                {/* Gift Wrapping Options */}
                <div className="space-y-4">
                  <h3 className="font-medium text-lg border-b pb-2">Gift Wrapping</h3>

                  <div className="grid gap-3">
                    {GIFT_WRAPPING_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        onClick={() => setGiftWrapping(option.value)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          giftWrapping === option.value
                            ? "border-primary bg-primary/5"
                            : "border-muted hover:border-muted-foreground/30"
                        }`}
                        data-testid={`gift-wrap-${option.value}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              giftWrapping === option.value ? "border-primary" : "border-muted-foreground"
                            }`}>
                              {giftWrapping === option.value && (
                                <div className="w-2 h-2 rounded-full bg-primary" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{option.label}</p>
                              <p className="text-sm text-muted-foreground">{option.description}</p>
                            </div>
                          </div>
                          {option.price > 0 && (
                            <span className="font-medium text-primary">+${option.price}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Gift Receipt Options */}
                <div className="space-y-4">
                  <h3 className="font-medium text-lg border-b pb-2">Gift Receipt</h3>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div>
                      <p className="font-medium">Include gift receipt (no pricing shown)</p>
                      <p className="text-sm text-muted-foreground">
                        A special receipt will be included that hides all pricing information
                      </p>
                    </div>
                    <Switch
                      checked={hideGiftReceipt}
                      onCheckedChange={setHideGiftReceipt}
                      data-testid="switch-hide-receipt"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex gap-4 pt-6">
            <Button variant="outline" onClick={() => handleBack(8)} className="flex-1" data-testid="button-back">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button onClick={handleContinue} className="flex-1" data-testid="button-continue">
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}

// Step 9: Review & Pay (Summary and Stripe checkout)
function Step9Payment({
  checkoutState,
  updateState,
  goToStep,
  handleBack,
  calculateTotal,
  onSubmit,
  onReserve,
  isPending,
}: {
  checkoutState: CheckoutState;
  updateState: (updates: Partial<CheckoutState>) => void;
  goToStep: (step: number) => void;
  handleBack: (fromStep: number) => void;
  calculateTotal: () => number;
  onSubmit: () => void;
  onReserve: () => void;
  isPending: boolean;
}) {
  // Local state for editable fields
  const [insurancePlan, setInsurancePlan] = useState<'monthly' | 'annual' | 'none'>(checkoutState.insurancePlan || 'monthly');
  const [paymentOption, setPaymentOption] = useState<'full' | 'reserve'>('full');

  // Editable fields
  const [biome, setBiome] = useState(checkoutState.biome);
  const [hasPremiumPlants, setHasPremiumPlants] = useState(checkoutState.hasPremiumPlants);
  const [hasCustomDesign, setHasCustomDesign] = useState(checkoutState.hasCustomDesign);
  const [experienceType, setExperienceType] = useState(checkoutState.experienceType);
  const [isGift, setIsGift] = useState(checkoutState.isGift);
  const [recipientName, setRecipientName] = useState(checkoutState.recipientName);
  const [giftMessage, setGiftMessage] = useState(checkoutState.giftMessage);

  // Delivery address fields
  const [deliveryAddress, setDeliveryAddress] = useState({
    street: '',
    city: checkoutState.cityName || '',
    state: checkoutState.state || 'CO',
    zip: '',
  });

  // Update parent state when fields change
  useEffect(() => {
    updateState({
      insurancePlan,
      wantsInsurance: insurancePlan !== 'none',
      biome,
      hasPremiumPlants,
      hasCustomDesign,
      experienceType,
      isGift,
      recipientName,
      giftMessage,
    });
  }, [insurancePlan, biome, hasPremiumPlants, hasCustomDesign, experienceType, isGift, recipientName, giftMessage]);

  const totalPrice = calculateTotal();

  const formatWeek = (weekStr: string) => {
    if (!weekStr) return "Not selected";
    const date = new Date(weekStr);
    const endDate = new Date(date);
    endDate.setDate(date.getDate() + 6);
    const formatDate = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${formatDate(date)} - ${formatDate(endDate)}, ${date.getFullYear()}`;
  };

  // Pricing breakdown
  const basePrice = 499;
  const hasExperience = experienceType !== 'none';
  const experiencePrice = experienceType === '2-person' ? 1501 : (experienceType === '4-person' ? 3501 : 0);
  const hasUpgrades = hasPremiumPlants || hasCustomDesign;
  const biomeDisplayName = biome === "forest" ? "Forest" : "Desert";
  const qualifiesForDelivery = checkoutState.qualifiesForDelivery;

  const getInsurancePriceDisplay = () => {
    switch (insurancePlan) {
      case 'monthly': return '$20/mo';
      case 'annual': return '$200/yr';
      case 'none': return 'None';
    }
  };

  const handleCheckout = () => {
    if (!qualifiesForDelivery && paymentOption === 'reserve') {
      onReserve();
    } else {
      onSubmit();
    }
  };

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
      >
        <div className="text-center mb-8">
          <CreditCard className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-serif text-3xl md:text-4xl font-semibold mb-3">Review & Pay</h1>
          <p className="text-muted-foreground">Review and finalize your purchase</p>
        </div>

        <Card className="p-6 md:p-8">
          {/* Order Summary */}
          <h3 className="font-serif text-xl font-semibold mb-4">Your Order</h3>
          <Separator className="mb-4" />

          {/* Base Terrarium */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              <span>Base Terrarium ({biomeDisplayName})</span>
            </div>
            <span className="font-medium">${basePrice}</span>
          </div>

          {/* Upgrades */}
          {hasUpgrades && (
            <>
              {hasPremiumPlants && (
                <div className="flex items-center justify-between py-2 pl-4">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    <span>Premium Rare Plants</span>
                  </div>
                  <span className="font-medium">+$499</span>
                </div>
              )}
              {hasCustomDesign && (
                <div className="flex items-center justify-between py-2 pl-4">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    <span>Custom 1:1 Design</span>
                  </div>
                  <span className="font-medium">+$999</span>
                </div>
              )}
            </>
          )}

          {/* Experience */}
          {hasExperience && (
            <div className="flex items-center justify-between py-2 pl-4">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-primary" />
                <span>{experienceType === "2-person" ? "2-Person Experience" : "4-Person Experience"}</span>
              </div>
              <span className="font-medium">+${experiencePrice.toLocaleString()}</span>
            </div>
          )}

          {/* Insurance display */}
          {insurancePlan !== 'none' && (
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" />
                <span>Plant Care Insurance</span>
              </div>
              <span className="font-medium text-muted-foreground">{getInsurancePriceDisplay()}</span>
            </div>
          )}

          {/* Gift indicator */}
          {isGift && recipientName && (
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <Gift className="w-4 h-4 text-primary" />
                <span>Gift for {recipientName}</span>
              </div>
            </div>
          )}

          <Separator className="my-4" />

          {/* Total */}
          <div className="flex justify-between items-center mb-2">
            <p className="text-lg font-semibold">Today's Total</p>
            <p className="font-serif text-2xl font-bold text-primary">
              ${qualifiesForDelivery || paymentOption === 'full' ? totalPrice.toLocaleString() : '99'}
            </p>
          </div>
          {insurancePlan !== 'none' && (
            <p className="text-sm text-muted-foreground text-right mb-2">
              + {getInsurancePriceDisplay()} insurance
            </p>
          )}

          {/* Delivery Week */}
          <div className="bg-muted/50 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">Delivery: {formatWeek(checkoutState.deliveryWeek)}</span>
            </div>
          </div>

          <Separator className="my-4" />

          {/* Customize Options Accordion */}
          <div className="mb-6">
            <p className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <Settings2 className="w-4 h-4" />
              Customize Your Order
            </p>

            <Accordion type="single" collapsible className="w-full">
              {/* Delivery Address */}
              <AccordionItem value="address">
                <AccordionTrigger className="text-sm hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-primary" />
                    <span>Delivery Address</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 pt-2">
                    <div>
                      <Label className="text-xs">Street Address</Label>
                      <Input
                        placeholder="123 Main St"
                        value={deliveryAddress.street}
                        onChange={(e) => setDeliveryAddress({ ...deliveryAddress, street: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">City</Label>
                        <Input
                          placeholder="Denver"
                          value={deliveryAddress.city}
                          onChange={(e) => setDeliveryAddress({ ...deliveryAddress, city: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">ZIP Code</Label>
                        <Input
                          placeholder="80202"
                          value={deliveryAddress.zip}
                          onChange={(e) => setDeliveryAddress({ ...deliveryAddress, zip: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Biome Selection */}
              <AccordionItem value="biome">
                <AccordionTrigger className="text-sm hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Leaf className="w-4 h-4 text-primary" />
                    <span>Biome: {biomeDisplayName}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <div
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        biome === 'desert' ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                      onClick={() => setBiome('desert')}
                    >
                      <Sun className="w-5 h-5 text-amber-500 mb-1" />
                      <p className="font-medium text-sm">Desert</p>
                    </div>
                    <div
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        biome === 'forest' ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                      onClick={() => setBiome('forest')}
                    >
                      <TreePine className="w-5 h-5 text-green-500 mb-1" />
                      <p className="font-medium text-sm">Forest</p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Upgrades */}
              <AccordionItem value="upgrades">
                <AccordionTrigger className="text-sm hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span>Upgrades {hasUpgrades ? `(${[hasPremiumPlants && 'Premium', hasCustomDesign && 'Custom'].filter(Boolean).join(', ')})` : '(None)'}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 pt-2">
                    <div
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        hasPremiumPlants ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                      onClick={() => setHasPremiumPlants(!hasPremiumPlants)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Checkbox checked={hasPremiumPlants} />
                          <span className="font-medium text-sm">Premium Rare Plants</span>
                        </div>
                        <span className="text-sm font-medium">+$499</span>
                      </div>
                    </div>
                    <div
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        hasCustomDesign ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                      onClick={() => setHasCustomDesign(!hasCustomDesign)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Checkbox checked={hasCustomDesign} />
                          <span className="font-medium text-sm">Custom 1:1 Design</span>
                        </div>
                        <span className="text-sm font-medium">+$999</span>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Experience */}
              <AccordionItem value="experience">
                <AccordionTrigger className="text-sm hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Mountain className="w-4 h-4 text-primary" />
                    <span>Experience: {experienceType === 'none' ? 'None' : experienceType === '2-person' ? '2-Person' : '4-Person'}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2 pt-2">
                    {(['none', '2-person', '4-person'] as const).map((type) => (
                      <div
                        key={type}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          experienceType === type ? 'border-primary bg-primary/5' : 'border-muted'
                        }`}
                        onClick={() => setExperienceType(type)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">
                            {type === 'none' ? 'No Experience' : type === '2-person' ? '2-Person Nature Experience' : '4-Person Nature Experience'}
                          </span>
                          <span className="text-sm font-medium">
                            {type === 'none' ? '$0' : type === '2-person' ? '+$1,501' : '+$3,501'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Gift Options */}
              <AccordionItem value="gift">
                <AccordionTrigger className="text-sm hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-primary" />
                    <span>Gift Options {isGift ? '(Enabled)' : '(Disabled)'}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Is this a gift?</Label>
                      <Switch checked={isGift} onCheckedChange={setIsGift} />
                    </div>
                    {isGift && (
                      <>
                        <div>
                          <Label className="text-xs">Recipient Name</Label>
                          <Input
                            placeholder="Who is this for?"
                            value={recipientName}
                            onChange={(e) => setRecipientName(e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Gift Message</Label>
                          <Textarea
                            placeholder="Write a personal message..."
                            value={giftMessage}
                            onChange={(e) => setGiftMessage(e.target.value)}
                            className="mt-1 min-h-[60px]"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Insurance */}
              <AccordionItem value="insurance">
                <AccordionTrigger className="text-sm hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <span>Insurance: {insurancePlan === 'monthly' ? 'Monthly ($20/mo)' : insurancePlan === 'annual' ? 'Annual ($200/yr)' : 'None'}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2 pt-2">
                    <div
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        insurancePlan === 'monthly' ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                      onClick={() => setInsurancePlan('monthly')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full border-2 ${insurancePlan === 'monthly' ? 'border-primary bg-primary' : 'border-muted-foreground'}`} />
                          <span className="font-medium text-sm">Monthly</span>
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Default</span>
                        </div>
                        <span className="text-sm font-medium">$20/mo</span>
                      </div>
                    </div>
                    <div
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        insurancePlan === 'annual' ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                      onClick={() => setInsurancePlan('annual')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full border-2 ${insurancePlan === 'annual' ? 'border-primary bg-primary' : 'border-muted-foreground'}`} />
                          <span className="font-medium text-sm">Annual</span>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Save $40</span>
                        </div>
                        <span className="text-sm font-medium">$200/yr</span>
                      </div>
                    </div>
                    <div
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        insurancePlan === 'none' ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                      onClick={() => setInsurancePlan('none')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full border-2 ${insurancePlan === 'none' ? 'border-primary bg-primary' : 'border-muted-foreground'}`} />
                          <span className="font-medium text-sm">No Insurance</span>
                        </div>
                        <span className="text-sm text-muted-foreground">$0</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 ml-5">I'll take full responsibility</p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Payment Option (only for non-qualifying users) */}
              {!qualifiesForDelivery && (
                <AccordionItem value="payment-option">
                  <AccordionTrigger className="text-sm hover:no-underline">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-primary" />
                      <span>Payment: {paymentOption === 'full' ? 'Pay in Full' : 'Reserve ($99)'}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-2">
                      <div
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          paymentOption === 'full' ? 'border-primary bg-primary/5' : 'border-muted'
                        }`}
                        onClick={() => setPaymentOption('full')}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full border-2 ${paymentOption === 'full' ? 'border-primary bg-primary' : 'border-muted-foreground'}`} />
                            <span className="font-medium text-sm">Pay in Full</span>
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Default</span>
                          </div>
                          <span className="text-sm font-medium">${totalPrice.toLocaleString()}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 ml-5">Complete your order now</p>
                      </div>
                      <div
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          paymentOption === 'reserve' ? 'border-primary bg-primary/5' : 'border-muted'
                        }`}
                        onClick={() => setPaymentOption('reserve')}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full border-2 ${paymentOption === 'reserve' ? 'border-primary bg-primary' : 'border-muted-foreground'}`} />
                            <span className="font-medium text-sm">Just Reserve</span>
                          </div>
                          <span className="text-sm font-medium">$99</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 ml-5">Hold your spot - fully refundable</p>
                      </div>
                    </div>
                    {paymentOption === 'reserve' && (
                      <div className="mt-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                        <p className="text-xs text-amber-800 dark:text-amber-200">
                          We'll let you know when delivery becomes available in your area. Your $99 is fully refundable if we can't deliver to you.
                        </p>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </div>

          {/* Payment Methods Available */}
          <div className="bg-muted/30 rounded-lg p-4 mb-4">
            <p className="text-xs text-muted-foreground text-center mb-3">Accepted payment methods</p>
            <div className="flex items-center justify-center gap-4">
              {/* Apple Pay */}
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-8 bg-black rounded flex items-center justify-center">
                  <svg className="w-6 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.72 13.34c-.02-1.81 1.51-2.68 1.58-2.73-.86-1.26-2.2-1.43-2.68-1.45-1.14-.12-2.23.67-2.81.67-.58 0-1.47-.65-2.42-.63-1.24.02-2.39.72-3.03 1.84-1.29 2.24-.33 5.56.93 7.38.62.89 1.35 1.9 2.32 1.86.93-.04 1.28-.6 2.4-.6 1.12 0 1.44.6 2.42.58.99-.02 1.62-.91 2.23-1.81.7-1.03.99-2.03 1.01-2.08-.02-.01-1.94-.74-1.96-2.95zm-1.85-5.43c.51-.62.86-1.48.77-2.34-.74.03-1.63.49-2.16 1.11-.48.55-.89 1.43-.78 2.27.82.06 1.66-.42 2.17-1.04z"/>
                  </svg>
                </div>
                <span className="text-[10px] text-muted-foreground">Apple Pay</span>
              </div>
              {/* Google Pay */}
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-8 bg-white border border-gray-200 rounded flex items-center justify-center">
                  <svg className="w-8 h-5" viewBox="0 0 40 16" fill="none">
                    <path d="M15.28 8.16v4.8h-1.52V1.04h4.04c.97 0 1.8.32 2.48.96.69.64 1.04 1.43 1.04 2.36 0 .95-.35 1.74-1.04 2.38-.67.64-1.5.96-2.48.96h-2.52zm0-5.6v4.08h2.55c.58 0 1.06-.19 1.44-.56.39-.38.58-.84.58-1.38 0-.53-.19-.98-.57-1.35-.38-.38-.86-.57-1.45-.57h-2.55v-.22z" fill="#5F6368"/>
                    <path d="M24.58 5.28c1.11 0 1.99.3 2.63.9.64.6.96 1.42.96 2.46v4.98h-1.45v-1.12h-.06c-.62.92-1.45 1.38-2.48 1.38-.88 0-1.61-.26-2.19-.78-.58-.52-.87-1.17-.87-1.95 0-.83.3-1.48.9-1.97.6-.49 1.4-.73 2.4-.73.85 0 1.55.16 2.1.47v-.33c0-.48-.19-.89-.57-1.22-.38-.33-.83-.49-1.35-.49-.78 0-1.39.33-1.84.99l-1.34-.84c.67-.99 1.66-1.49 2.97-1.49v-.26zm-1.85 5.7c0 .36.15.66.46.9.3.24.66.36 1.07.36.58 0 1.09-.21 1.53-.64.44-.43.66-.92.66-1.48-.45-.36-1.07-.54-1.87-.54-.58 0-1.07.14-1.45.42-.39.28-.58.62-.58 1.02l.18-.04z" fill="#5F6368"/>
                    <path d="M36.4 5.52l-5.07 11.64h-1.57l1.88-4.1-3.34-7.54h1.65l2.4 5.74h.03l2.34-5.74H36.4z" fill="#5F6368"/>
                    <path d="M10.14 7.07c0-.45-.04-.88-.11-1.29H5.18v2.44h2.78c-.12.64-.49 1.19-1.04 1.56v1.29h1.68c.99-.91 1.56-2.25 1.56-3.83l-.02-.17z" fill="#4285F4"/>
                    <path d="M5.18 12.32c1.41 0 2.59-.46 3.45-1.26l-1.68-1.3c-.47.31-1.06.5-1.77.5-1.36 0-2.51-.92-2.92-2.15H.48v1.34c.86 1.7 2.61 2.87 4.7 2.87z" fill="#34A853"/>
                    <path d="M2.26 8.12c-.1-.31-.17-.64-.17-.98 0-.34.06-.67.17-.98V4.82H.48c-.35.69-.55 1.47-.55 2.32 0 .85.2 1.63.55 2.32l1.78-1.34z" fill="#FBBC05"/>
                    <path d="M5.18 4.01c.77 0 1.46.26 2 .78l1.5-1.5C7.76 2.45 6.58 1.96 5.18 1.96c-2.09 0-3.84 1.17-4.7 2.87l1.78 1.38c.41-1.23 1.56-2.15 2.92-2.15v-.05z" fill="#EA4335"/>
                  </svg>
                </div>
                <span className="text-[10px] text-muted-foreground">Google Pay</span>
              </div>
              {/* Cards */}
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded flex items-center justify-center">
                  <CreditCard className="w-5 h-4 text-white" />
                </div>
                <span className="text-[10px] text-muted-foreground">Cards</span>
              </div>
              {/* Link */}
              <div className="flex flex-col items-center gap-1">
                <div className="w-12 h-8 bg-emerald-500 rounded flex items-center justify-center">
                  <svg className="w-6 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 12c0-1.1.9-2 2-2h8c1.1 0 2 .9 2 2s-.9 2-2 2H8c-1.1 0-2-.9-2-2z"/>
                    <path d="M4 8h16v8H4V8z" stroke="currentColor" strokeWidth="2" fill="none"/>
                  </svg>
                </div>
                <span className="text-[10px] text-muted-foreground">Link</span>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <Button
            onClick={handleCheckout}
            className="w-full"
            size="lg"
            disabled={isPending}
            data-testid="button-complete-purchase"
          >
            {isPending ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Processing...
              </span>
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                {!qualifiesForDelivery && paymentOption === 'reserve' ? 'Reserve - $99' : `Checkout - $${totalPrice.toLocaleString()}`}
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-3">
            {!qualifiesForDelivery && paymentOption === 'reserve'
              ? 'Fully refundable if we can\'t deliver to your area'
              : 'You\'ll be redirected to secure Stripe checkout'
            }
          </p>

          <div className="flex gap-4 pt-4">
            <Button
              variant="ghost"
              onClick={() => handleBack(9)}
              className="w-full"
              disabled={isPending}
              data-testid="button-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-4">
            Secure checkout powered by Stripe.
          </p>
        </Card>
      </motion.div>
    </div>
  );
}
